import { Type } from '@sinclair/typebox';

import { VOUCHER_CODE_MAX_LENGTH } from './constants';
import {
  Recipe,
  RecipeBusinessType,
  RecipeFavorite,
  RecipeIngredient,
  RecipeIngredientGroup,
  RecipeMedia,
  RecipeNotifications,
  RecipeProduct,
  RecipeSection,
  RecipeSectionItem,
  RecipeTag,
  RecipeTagging,
} from './database.recipe';
import { Nullable, RecursiveStatic } from './generics';

// ---------------------------------------------------------------------------
// User & Auth
// ---------------------------------------------------------------------------
export const User = Type.Object({
  id: Type.String({ format: 'uuid' }),
  sol_id: Type.Number(),
  account_number: Type.Number(),
  division: Type.String(),
  payment_method: Type.Union([
    Type.Literal('cash'),
    Type.Literal('term_of_payment'),
  ]),
  term_of_payment_days: Type.Optional(
    Type.Union([
      Type.Literal(7),
      Type.Literal(14),
      Type.Literal(21),
      Type.Literal(30),
      Type.Literal(45),
      Type.Literal(60),
      Type.Null(),
    ])
  ),
  credit_limit: Type.Number(),
  email: Type.String({ format: 'email' }),
  role: Type.Union([
    Type.Literal('admin'),
    Type.Literal('super_admin'),
    Type.Literal('cs_katalog'),
    Type.Literal('cs_cabang'),
    Type.Literal('collector'),
    Type.Literal('buyer_bill_to'),
    Type.Literal('buyer_ship_to'),
  ]),
  name: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  updated_at: Type.Integer({ minimum: 0 }),
  updated_by: Nullable(Type.String({ format: 'uuid' })),
  deleted_at: Type.Integer({ minimum: 0 }),
  deleted_by: Nullable(Type.String({ format: 'uuid' })),
  catalog: Type.Union([
    Type.Literal('all'),
    Type.Literal('trading'),
    Type.Literal('fine_food'),
    Type.Literal('diamond'),
    Type.Literal('equipment'),
    Type.Literal('buyer'),
    Type.Null(),
  ]),
  last_name: Type.String(),
  first_name: Type.String(),
  status: Type.Union([Type.Literal('active'), Type.Literal('inactive')]),
  parent_id: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
  email_status: Type.Union([
    Type.Literal('approved'),
    Type.Literal('pending'),
    Type.Literal('reject'),
    Type.Null(),
  ]),
  request_admin_status: Type.Union([
    Type.Literal('approved'),
    Type.Literal('waiting'),
    Type.Literal('reject'),
    Type.Null(),
  ]),
  registration_status: Type.Optional(Nullable(Type.String({ maxLength: 30 }))),
  phone_number: Type.Optional(Nullable(Type.String({ maxLength: 20 }))),
  service_area_id: Type.Optional(Nullable(Type.String({ format: 'uuid' }))),
  business_segmentation_id: Type.Optional(
    Nullable(Type.String({ format: 'uuid' }))
  ),
  otp_hash: Type.Optional(Nullable(Type.String())),
  otp_expires_at: Type.Optional(Nullable(Type.Integer({ minimum: 0 }))),
  otp_verified_at: Type.Optional(Nullable(Type.Integer({ minimum: 0 }))),
  otp_resend_count: Type.Optional(Type.Integer({ minimum: 0 })),
  otp_verify_attempt_count: Type.Optional(Type.Integer({ minimum: 0 })),
  last_otp_sent_at: Type.Optional(Nullable(Type.Integer({ minimum: 0 }))),
  registration_token_hash: Type.Optional(Nullable(Type.String())),
  registration_submitted_at: Type.Optional(
    Nullable(Type.Integer({ minimum: 0 }))
  ),
  mdg_reference: Type.Optional(Nullable(Type.String({ maxLength: 64 }))),
  mdg_ticket_number: Type.Optional(Nullable(Type.String({ maxLength: 64 }))),
  mdg_last_error: Type.Optional(Nullable(Type.String())),
  mdg_attempt_started_at: Type.Optional(Nullable(Type.Integer({ minimum: 0 }))),
  mdg_attempt_count: Type.Optional(Type.Integer({ minimum: 0 })),
});

export const ResetPasswordToken = Type.Object({
  hash: Type.String({ minLength: 1 }),
  email: Type.String({ format: 'email' }),
  expiry: Type.Integer({ minimum: 0 }),
});

export const SetPasswordToken = Type.Object({
  hash: Type.String({ minLength: 1 }),
  email: Type.String({ format: 'email' }),
});

export const UserBillTo = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  bilto_id: Nullable(Type.String({ format: 'uuid' })),
  code: Nullable(Type.String()),
  company_name: Nullable(Type.String()),
  name: Nullable(Type.String()),
  addresses: Nullable(Type.String()),
  city: Nullable(Type.String()),
  postal_code: Nullable(Type.String()),
  is_blocked: Nullable(Type.Boolean({ default: false })),
  created_at: Type.Any(),
  updated_at: Type.Any(),
});

export const BilTo = Type.Object({
  id: Type.String({ format: 'uuid' }),
  id_kil_bilto: Type.String(),
  search_term_bilto_2: Type.String(),
  cpy_title_bilto: Type.String(),
  lang_bilto: Type.String(),
  company_name_bilto: Type.String(),
  first_name_bilto: Type.String(),
  id_no_bilto: Type.String(),
  mid_name_bilto: Type.String(),
  last_name_bilto: Type.String(),
  email_bilto: Type.String(),
  type_bilto: Type.String(),
  div_bilto: Type.String(),
  dept_bilto: Type.String(),
  function_bilto: Type.String(),
  telp_bilto: Type.String(),
  telp_bilto2: Type.String(),
  telp_bilto3: Type.String(),
  hp_bilto: Type.String(),
  hp_bilto2: Type.String(),
  hp_bilto3: Type.String(),
  fax_bilto: Type.String(),
  lm_address1_bilto: Type.String(),
  address1_bilto: Type.String(),
  address2_bilto: Type.String(),
  zip_code_bilto: Type.String(),
  city_bilto: Type.String(),
  prov_bilto: Type.String(),
  district_bilto: Type.String(),
  village_bilto: Type.String(),
  timezone_bilto: Type.String(),
  freezer_bilto: Type.String(),
  vat_bilto: Type.String(),
  atribut1_bilto: Type.String(),
  atribut2_bilto: Type.String(),
  dist_ch_bilto: Type.String(),
  sales_org_bilto: Type.String(),
  sales_district_bilto: Type.String(),
  crm_limit_bilto: Type.String(),
  top_bilto: Type.String(),
  tax_bilto: Type.String(),
  payment_method_bilto: Type.String(),
  acc_no_bilto: Type.String(),
  acc_name_bilto: Type.String(),
  bank_name_bilto: Type.String(),
  acc_branch_bilto: Type.String(),
  bank_key_bilto: Type.String(),
  created_date: Type.String(),
  created_by: Type.String(),
  creator_dist_ch: Type.String(),
  sap_no_bilto: Type.Integer(),
  req_sts_bilto: Type.String(),
  reas_bilto: Type.String(),
  rej_by_bilto: Type.String(),
  active_bilto: Type.String(),
  block_bilto: Type.String(),
  mstr_appr_sts_bilto: Type.String(),
  mstr_appr_date_bilto: Type.String(),
  mstr_appr_by_bilto: Type.String(),
  sc_company_name_bilto: Type.String(),
  flag_col_submit: Type.String(),
  flag_distr_submit: Type.String(),
  id_crlim_cr_dependency: Type.String(),
  final_appr_time_bilto: Type.String(),
  acct_appr_time_bilto: Type.String(),
  mstr_appr_time_bilto: Type.String(),
  created_time: Type.String(),
  tipe_id: Type.String(),
  atribut3_bilto: Type.String(),
  no_spkp: Type.String(),
  first_name_sol: Type.String(),
  last_name_sol: Type.String(),
  email_sol: Type.String(),
  flag_sol: Type.String(),
  telp_sol: Type.String(),
  top_desc: Type.String(),
  final_appr_sts_bilto: Type.String(),
  final_appr_date_bilto: Type.String(),
  final_appr_by_bilto: Type.String(),
  acct_appr_sts_bilto: Type.String(),
  acct_appr_date_bilto: Type.String(),
  acct_appr_by_bilto: Type.String(),
  final_appr_sts_olb: Type.String(),
  final_appr_by_olb: Type.String(),
  final_appr_time_olb: Type.String(),
  dw_status_bilto: Type.String(),
  orac_no_bilto: Type.String(),
  stop_warn: Type.String(),
  sfa: Type.String(),
  final_appr_date_olb: Type.String(),
  created_sol: Type.String(),
  created_sol_date: Nullable(Type.String({ format: 'date' })),
  last_update: Type.String(),
  flag_dummy: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const ShipTo = Type.Object({
  id: Type.String({ format: 'uuid' }),
  code: Type.String(),
  name: Type.String({ minLength: 1 }),
  address: Type.String(),
  city: Type.String(),
  postal_code: Type.String(),
  status: Type.Union([
    Type.Literal('active'),
    Type.Literal('non-active'),
    Type.Literal('block'),
  ]),
  cg_1_business_segmentation: Type.String(),
  cg_2_business_category: Type.String(),
  cg_3_business_type: Type.String(),
  cg_4_manufacture: Type.String(),
  cg_5_trading: Type.String(),
  cg_1_business_segmentation_desc: Type.String(),
  cg_2_business_category_desc: Type.String(),
  cg_3_business_type_desc: Type.String(),
  cg_4_manufacture_desc: Type.String(),
  cg_5_trading_desc: Type.String(),
  distribution_channel: Type.String(),
  distribution_channel_desc: Type.String(),
  pic_name: Type.String(),
  province: Nullable(Type.String()),
  country: Nullable(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Type.Integer({ minimum: 0 }),
  external_id: Type.Optional(Nullable(Type.String())),
  id_kil_bilto: Type.String(),
  id_kil_shipto: Type.String(),
  shipto_type: Type.String(),
  cpy_title_shipto: Type.String(),
  first_name_shipto: Type.String(),
  last_name_shipto: Type.String(),
  search_term_shipto: Type.String(),
  search_term_shipto_2: Type.String(),
  div_shipto: Type.String(),
  telp_shipto: Type.String(),
  telp_shipto2: Type.String(),
  telp_shipto3: Type.String(),
  hp_shipto: Type.String(),
  hp_shipto2: Type.String(),
  hp_shipto3: Type.String(),
  email_shipto: Type.String(),
  lm_addrees1_shipto: Type.String(),
  adddres2_shipto: Type.String(),
  district_shipto: Type.String(),
  village_shipto: Type.String(),
  billing_address: Type.String(),
  salesman_shipto_trd: Type.String(),
  sales_id_shipto: Type.String(),
  spv_salesman_shipto: Type.String(),
  spv_sales_id_shipto: Type.String(),
  sof_shipto: Type.String(),
  deliv_plant_shipto: Type.String(),
  sales_org_shipto: Type.String(),
  sales_district_shipto: Type.String(),
  attribut2_shipto: Type.String(),
  kam_code_dia_shipto: Type.String(),
  kam_code_trd_shipto: Type.String(),
  kae_code_dia_shipto: Type.String(),
  kae_code_trd_shipto: Type.String(),
  time_zone_shipto: Type.String(),
  tranz_zone_shipto: Type.String(),
  priority_shipto: Type.String(),
  freezer_bilto: Type.String(),
  pod_bilto: Type.String(),
  col_bilto: Type.String(),
  created_date: Type.String(),
  created_date_time: Type.String(),
  created_by: Type.String(),
  creator_dist_ch: Type.String(),
  deliv_date_shipto: Type.String(),
  long_shipto: Type.String(),
  lat_shipto: Type.String(),
  hp_pic_col_shipto: Type.String(),
  coll_day_shipto01: Type.String(),
  coll_day_shipto02: Type.String(),
  coll_day_shipto03: Type.String(),
  coll_day_shipto04: Type.String(),
  coll_day_shipto05: Type.String(),
  coll_day_shipto06: Type.String(),
  dw_sts_shipto: Type.String(),
  sap_no_shipto: Type.Integer(),
  orac_no_shipto: Type.String(),
  req_sts_shipto: Type.String(),
  reas_shipto: Type.String(),
  rej_by_shipto: Type.String(),
  changed_type_shipto: Type.String(),
  kode_sales_fs_trd: Type.String(),
  id_sales_fs_trd: Type.String(),
  kode_spv_sales_fs_trd: Type.String(),
  id_spv_sales_fs_trd: Type.String(),
  kode_pricelist_fs_trd: Type.String(),
  kode_sales_fs_dia: Type.String(),
  id_sales_fs_dia: Type.String(),
  kode_spv_sales_fs_dia: Type.String(),
  id_spv_sales_fs_dia: Type.String(),
  kode_pricelist_fs_dia: Type.String(),
  mstr_appr_sts_shipto: Type.String(),
  same_as_bilto: Type.String(),
  sc_company_name_shipto: Type.String(),
  block_shipto: Type.String(),
  flag_show_pepperi: Type.String(),
  stop_warn_credit_limit: Type.String(),
  message: Type.String(),
  fiscal_address: Type.String(),
  tfiscal_adddress: Type.String(),
  colector_shipto: Type.String(),
  flag_dummy: Type.Union([Type.String(), Type.Null()]),
  attribut9_shipto: Type.String(),
  grouping_shipto: Type.String(),
  tax_shipto: Type.String(),
});

export const UserShipTo = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  ship_to_id: Type.String({ format: 'uuid' }),
});

export const ShipToContact = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  ship_to_id: Type.String({ format: 'uuid' }),
  name: Nullable(Type.String()),
  telp: Nullable(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const UserRegistrationTickets = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.Optional(Type.String({ format: 'uuid' })),
  no_ticket: Type.String(),
  status: Type.Boolean(),
  email: Type.String(),
  first_name: Type.String(),
  last_name: Type.String(),
  sap_no_bilto: Type.String(),
  dist_channel: Type.String(),
  telp: Type.String(),
  topic: Type.String(),
  is_processed: Type.Boolean(),
  company_name: Type.Optional(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const Session = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  subscriber: Type.Union([
    Type.Literal('web_buyer'),
    Type.Literal('web_admin'),
    Type.Literal('mobile'),
  ]),
  client_type: Type.Optional(
    Type.Union([
      Type.Literal('android'),
      Type.Literal('iphone'),
      Type.Literal('web'),
      Type.Null(),
    ])
  ),
  client_os: Type.Optional(
    Type.Union([
      Type.Literal('windows'),
      Type.Literal('linux'),
      Type.Literal('macos'),
      Type.Literal('android'),
      Type.Literal('ios'),
      Type.Null(),
    ])
  ),
  client_device: Type.Optional(Type.String()),
  client_version: Type.Optional(Type.String()),
  app_version: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  push_notification_token: Type.Optional(Type.String()),
  created_at: Type.Integer({ exclusiveMinimum: 0 }),
  expired_at: Type.Integer({ exclusiveMinimum: 0 }),
});

// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------
export const ProductCategory = Type.Object({
  id: Type.String(),
  name: Type.String(),
  icon: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  updated_at: Type.Integer({ minimum: 0 }),
  updated_by: Nullable(Type.String({ format: 'uuid' })),
  deleted_at: Type.Integer({ minimum: 0 }),
  deleted_by: Nullable(Type.String({ format: 'uuid' })),
});

export const ProductSubCategory = Type.Object({
  id: Type.String(),
  name: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  updated_at: Type.Integer({ minimum: 0 }),
  updated_by: Nullable(Type.String({ format: 'uuid' })),
  deleted_at: Type.Integer({ minimum: 0 }),
  deleted_by: Nullable(Type.String({ format: 'uuid' })),
});

export const ProductSubThirdCategory = Type.Object({
  id: Type.String(),
  name: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Type.Integer({ minimum: 0 }),
});

export const Product = Type.Object({
  id: Type.String({ format: 'uuid' }),
  title: Type.String(),
  slug: Type.Optional(Nullable(Type.String())),
  display_title: Type.Optional(Nullable(Type.String())),
  desc: Type.String(),
  type: Type.String(),
  pack_unit: Type.String(),
  information: Type.String(),
  composition: Type.String(),
  superiority: Type.String(),
  item_code: Type.Number(),
  brand_name: Type.String(),
  brand_desc: Type.String(),
  category_id: Type.String(),
  sub_category_id: Type.String(),
  third_sub_category_id: Type.String(),
  material_division: Type.Union([
    Type.Literal('diamond'),
    Type.Literal('trading'),
    Type.Literal('fnb'),
    Type.Literal('equipment'),
  ]),
  can_overwrite_price: Type.Boolean(),
  category_2: Type.String(),
  tag: Type.String(),
  division: Type.Union([
    Type.Literal('diamond'),
    Type.Literal('trading'),
    Type.Literal('fnb'),
    Type.Literal('equipment'),
  ]),
  product_storage: Type.Union([
    Type.Literal('frozen'),
    Type.Literal('dry'),
    Type.Literal('dry_ac'),
    Type.Literal('chilled'),
  ]),
  transportation_group: Type.String(),
  barcode: Type.String(),
  measurement_unit: Type.String(),
  measurement_unit_desc: Type.String(),
  sales_unit: Type.String(),
  sales_unit_desc: Type.String(),
  dimension: Type.String(),
  dimension_unit: Type.String(),
  gross_weight: Type.Number(),
  net_weight: Type.Number(),
  weight_unit: Type.String(),
  gross_weight_desc: Type.String(),
  net_weight_desc: Type.String(),
  volume: Type.Number(),
  volume_unit: Type.String(),
  flag_fs: Type.Boolean(),
  flag_gt: Type.Boolean(),
  flag_sts: Type.String(),
  flag_show_pepperi: Type.Boolean(),
  is_valid_data: Type.Boolean(),
  finance_category_5: Type.String(),
  finance_category_6: Type.String(),
  finance_category_7: Type.String(),
  suggestion_1: Type.String(),
  suggestion_2: Type.String(),
  minimum_order_quantity: Type.Integer(),
  order_quantity_multiple: Type.Integer(),
  is_only_for_sukanda_partner: Type.Boolean(),
  is_only_for_online_business: Type.Boolean(),
  is_eligible_for_so_automation: Type.Boolean(),
  is_eligible_for_do_automation: Type.Optional(Type.Boolean()),
  total_shelf_life: Type.Optional(Nullable(Type.Integer())),
  min_remaining_shelf_life: Type.Optional(Nullable(Type.Integer())),
  ind_shelf_life_exp_date: Type.Optional(Nullable(Type.String())),
  product_group: Type.Optional(Nullable(Type.String())),
  created_at: Type.Integer({ minimum: 0 }),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  updated_at: Type.Integer({ minimum: 0 }),
  updated_by: Nullable(Type.String({ format: 'uuid' })),
  deleted_at: Type.Integer({ minimum: 0 }),
  deleted_by: Nullable(Type.String({ format: 'uuid' })),
  conversion_qty: Type.Number(),
  conversion_unit: Type.String(),
  conversion_sales_unit: Type.String(),
});

export const ProductImage = Type.Object({
  id: Type.String({ format: 'uuid' }),
  image: Type.String(),
  product_item_code: Type.Integer(),
  created_at: Type.Integer({ minimum: 0 }),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  updated_at: Type.Integer({ minimum: 0 }),
  updated_by: Nullable(Type.String({ format: 'uuid' })),
  deleted_at: Type.Integer({ minimum: 0 }),
  deleted_by: Nullable(Type.String({ format: 'uuid' })),
  thumbnail_url: Nullable(Type.String()),
  medium_image_url: Nullable(Type.String()),
  large_image_url: Nullable(Type.String()),
  index: Type.Integer(),
});

export const ProductKeyword = Type.Object({
  id: Type.String({ format: 'uuid' }),
  name: Type.String(),
  url: Type.String({ format: 'uri' }),
  index: Type.Integer({ minimum: 1, maximum: 5 }),
  business_segmentation: Type.Array(Type.String()),
  distribution_channel: Type.Array(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Type.Integer({ minimum: 0 }),
});

export const ProductHighlight = Type.Object({
  id: Type.String({ format: 'uuid' }),
  title: Type.String(),
  index: Type.Integer({ minimum: 1 }),
  business_segmentation: Type.Array(Type.String()),
  distribution_channel: Type.Array(Type.String()),
  product_highlight_type: Type.Optional(
    Type.Union([Type.Literal('general'), Type.Literal('specific')], {
      default: 'general',
    })
  ),
  created_at: Type.Integer({ minimum: 0 }),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  updated_at: Type.Integer({ minimum: 0 }),
  updated_by: Nullable(Type.String({ format: 'uuid' })),
  deleted_at: Type.Integer({ minimum: 0 }),
  deleted_by: Nullable(Type.String({ format: 'uuid' })),
});

export const ProductHighlightItem = Type.Object({
  id: Type.String({ format: 'uuid' }),
  product_highlight_id: Type.String({ format: 'uuid' }),
  product_id: Type.String({ format: 'uuid' }),
  index: Type.Optional(Type.Integer({ minimum: 0 })),
  created_at: Type.Integer({ minimum: 0 }),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  updated_at: Type.Integer({ minimum: 0 }),
  updated_by: Nullable(Type.String({ format: 'uuid' })),
  deleted_at: Type.Integer({ minimum: 0 }),
  deleted_by: Nullable(Type.String({ format: 'uuid' })),
});

export const ProductHighlightShipto = Type.Object({
  id: Type.String({ format: 'uuid' }),
  product_highlight_id: Type.String({ format: 'uuid' }),
  sap_no_shipto: Type.Integer(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Type.Integer({ minimum: 0 }),
});

export const ProductHighlightBusinessType = Type.Object({
  id: Type.String({ format: 'uuid' }),
  product_highlight_id: Type.String({ format: 'uuid' }),
  cg_3_business_type: Type.String(),
  cg_3_business_type_desc: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Type.Integer({ minimum: 0 }),
});

export const ProductPrice = Type.Object({
  id: Type.String({ format: 'uuid' }),
  price: Type.Number(),
  min_order_quantity: Type.Integer(),
  product_id: Type.String({ format: 'uuid' }),
  created_at: Type.Integer({ minimum: 0 }),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  updated_at: Type.Integer({ minimum: 0 }),
  updated_by: Nullable(Type.String({ format: 'uuid' })),
  deleted_at: Type.Integer({ minimum: 0 }),
  deleted_by: Nullable(Type.String({ format: 'uuid' })),
});

export const ProductValuationType = Type.Object({
  id: Type.String({ format: 'uuid' }),
  product_item_code: Type.Integer(),
  valuation_type: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const ProductExcludeBranch = Type.Object({
  id: Type.String({ format: 'uuid' }),
  product_item_code: Type.Integer(),
  distribution_channel: Type.String(),
  plant: Type.Integer(),
  cg_1_business_segmentation: Type.Optional(Nullable(Type.String())),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const ProductExcludeCustomer = Type.Object({
  id: Type.String({ format: 'uuid' }),
  product_item_code: Type.Integer(),
  sap_no_shipto: Type.Integer(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const ProductExcludePublic = Type.Object({
  id: Type.String({ format: 'uuid' }),
  product_item_code: Type.Integer(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
});

export const ProductPriorityPublic = Type.Object({
  id: Type.String({ format: 'uuid' }),
  product_item_code: Type.Integer(),
  index: Type.Integer({ minimum: 0 }),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
});

export const ProductCommonPrice = Type.Object({
  id: Type.String({ format: 'uuid' }),
  price_id: Type.String(),
  product_item_code: Type.Integer(),
  distribution_channel: Type.String(),
  cg_1_business_segmentation: Type.String(),
  cg_2_business_category: Nullable(Type.String()),
  cg_3_business_type: Nullable(Type.String()),
  cg_4_manufacture: Type.String(),
  cg_5_trading: Type.String(),
  valuation_type: Type.String(),
  price: Type.Number(),
  valid_from: Type.Number(),
  valid_until: Type.Number(),
  sap_updated_at: Type.Number(),
  uom: Nullable(Type.String()),
  price_order: Nullable(Type.Integer()),
});

export const ProductSpecialPrice = Type.Object({
  id: Type.String({ format: 'uuid' }),
  price_id: Type.String(),
  product_item_code: Type.Integer(),
  sap_no_shipto: Type.Integer(),
  distribution_channel: Type.String(),
  valuation_type: Type.String(),
  price: Type.Number(),
  valid_from: Type.Number(),
  valid_until: Type.Number(),
  sap_updated_at: Type.Number(),
});

export const ShiptoOrderValuationType = Type.Object({
  id: Type.String({ format: 'uuid' }),
  sap_no_shipto: Type.Integer(),
  product_item_code: Type.Integer(),
  valuation_type: Nullable(Type.String()),
  latest_order_id: Type.String({ format: 'uuid' }),
  latest_order_created_at: Type.Integer({ minimum: 0 }),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const ProductStock = Type.Object({
  id: Type.String({ format: 'uuid' }),
  product_item_code: Type.Integer(),
  distribution_channel: Type.String(),
  plant: Type.Integer(),
  storage_location: Type.String(),
  stock: Type.Number(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const ProductStockDay = Type.Object({
  id: Type.String({ format: 'uuid' }),
  product_item_code: Type.Integer(),
  distribution_channel: Type.String(),
  plant: Type.Integer(),
  storage_location: Type.String(),
  stock_days: Type.Integer(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const RfcProductStock = Type.Object({
  id: Type.String({ format: 'uuid' }),
  product_item_code: Type.Integer(),
  distribution_channel: Type.String(),
  status: Type.Union([
    Type.Literal('requested'),
    Type.Literal('processed'),
    Type.Literal('failed'),
    Type.Literal('completed'),
    Type.Literal('timeout'),
    Type.Literal('dead_letter'),
  ]),
  quantity: Type.Optional(Type.Number()),
  uom: Type.Optional(Type.String()),
  plant: Type.Optional(Type.String()),
  storage_location: Type.Optional(Type.String()),
  raw_response: Type.Record(Type.String(), Type.Unknown()), // JSONB field
  retry_count: Type.Optional(Type.Integer({ minimum: 0 })),
  processed_at: Type.Optional(Type.Integer({ minimum: 0 })), // assuming epoch timestamp
  error_message: Type.Optional(Type.String()),
  consumer_id: Type.Optional(Type.String({ maxLength: 255 })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const ProductPareto = Type.Object({
  id: Type.String({ format: 'uuid' }),
  product_item_code: Type.Integer(),
  distribution_channel: Type.String(),
  is_active: Type.Boolean(),
  created_at: Type.Integer({ minimum: 0 }), // Epoch timestamp
  updated_at: Type.Integer({ minimum: 0 }), // Epoch timestamp
});

export const ProductManualProcessConfiguration = Type.Object({
  id: Type.String({ format: 'uuid' }),
  distribution_channel: Type.String(),
  product_item_code: Type.Integer(),
  quantity: Type.Integer(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Type.Integer({ minimum: 0 }),
});

export const ProductDailyOrderLimitConfiguration = Type.Object({
  id: Type.String({ format: 'uuid' }),
  distribution_channel: Type.String(),
  product_item_code: Type.Integer(),
  daily_limit: Type.Integer({ minimum: 0 }),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const ProductAllowedPaymentMethod = Type.Union([
  Type.Literal('cash_on_delivery'),
  Type.Literal('bank_transfer'),
]);

export const ProductPaymentMethodConfiguration = Type.Object({
  id: Type.String({ format: 'uuid' }),
  distribution_channel: Type.String(),
  product_item_code: Type.Integer(),
  payment_method: ProductAllowedPaymentMethod,
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const ProductManualExcludeShiptoConfiguration = Type.Object({
  id: Type.String({ format: 'uuid' }),
  sap_no_shipto: Type.Integer(),
  product_item_code: Type.Integer(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const ProductManualShiptoConfiguration = Type.Object({
  id: Type.String({ format: 'uuid' }),
  sap_no_shipto: Type.Integer(),
  product_item_code: Type.Integer(),
  quantity: Type.Integer(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const DeliveryOrderAutomationConfiguration = Type.Object({
  id: Type.String({ format: 'uuid' }),
  timezone: Type.String(),
  operational_start_hour: Type.String({
    pattern: '^([01]\\d|2[0-3]):[0-5]\\d:[0-5]\\d$',
  }),
  operational_end_hour: Type.String({
    pattern: '^([01]\\d|2[0-3]):[0-5]\\d:[0-5]\\d$',
  }),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const DeliveryOrderAutomationEligibility = Type.Object({
  id: Type.String({ format: 'uuid' }),
  tranz_zone_shipto: Type.String(),
  distribution_channel: Type.String(),
  timezone: Type.String(),
  is_eligible: Type.Boolean(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const DeliveryOrderAutomationExcludeCustomer = Type.Object({
  id: Type.String({ format: 'uuid' }),
  sap_no_shipto: Type.String(),
  reason: Type.Optional(Nullable(Type.String())),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

// ---------------------------------------------------------------------------
// Order
// ---------------------------------------------------------------------------
export const OrderMeta = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user: Type.Omit(User, [
    'created_at',
    'updated_at',
    'deleted_at',
    'created_by',
    'updated_by',
    'deleted_by',
  ]),
  ship_to: Type.Pick(ShipTo, [
    'id',
    'city',
    'code',
    'name',
    'status',
    'address',
    'pic_name',
    'telp_shipto',
    'cg_5_trading',
    'sap_no_shipto',
    'cg_4_manufacture',
    'sales_org_shipto',
    'distribution_channel',
    'cg_1_business_segmentation',
    'flag_dummy',
  ]),
  shipto_id: Type.String({ format: 'uuid' }),
  sap_no_shipto: Type.Optional(Type.Integer()),
  user_id: Type.Optional(Type.String({ format: 'uuid' })),
  bill_to: Type.Optional(
    Nullable(
      Type.Record(
        Type.String(),
        Type.Union([Type.String(), Type.Number(), Type.Null(), Type.Boolean()])
      )
    )
  ),
  delivery_note: Type.Optional(Nullable(Type.String())),
  purchase_order_docs: Type.Optional(Nullable(Type.String())),
  payment_method: Type.Optional(
    Type.Union([
      Type.Literal('bank_transfer'),
      Type.Literal('cash_on_delivery'),
      Type.Literal('term_of_payment'),
      Type.Null(),
    ])
  ),
  term_of_payment_days: Type.Optional(
    Type.Union([
      Type.Literal(7),
      Type.Literal(14),
      Type.Literal(21),
      Type.Literal(30),
      Type.Literal(45),
      Type.Literal(60),
      Type.Null(),
    ])
  ),
  transaction_id: Type.Optional(Type.String()),
  is_submitted: Type.Boolean({ default: false }),
  total_items: Type.Number(),
  total: Type.Number(), // final total
  original_total: Type.Integer(),
  saved_total: Type.Integer(),
  checkout_original_total: Type.Optional(Nullable(Type.Integer())),
  checkout_total: Type.Optional(Nullable(Type.Integer())),
  checkout_saved_total: Type.Optional(Nullable(Type.Integer())),
  processed_original_total: Type.Optional(Nullable(Type.Integer())),
  processed_total: Type.Optional(Nullable(Type.Integer())),
  processed_saved_total: Type.Optional(Nullable(Type.Integer())),
  delivered_original_total: Type.Optional(Nullable(Type.Integer())),
  delivered_total: Type.Optional(Nullable(Type.Integer())),
  delivered_saved_total: Type.Optional(Nullable(Type.Integer())),
  received_original_total: Type.Optional(Nullable(Type.Integer())),
  received_total: Type.Optional(Nullable(Type.Integer())),
  received_saved_total: Type.Optional(Nullable(Type.Integer())),
  latest_original_total: Type.Optional(Nullable(Type.Integer())),
  latest_total: Type.Optional(Nullable(Type.Integer())),
  latest_saved_total: Type.Optional(Nullable(Type.Integer())),
  checkout_total_items: Type.Optional(Nullable(Type.Integer())),
  processed_total_items: Type.Optional(Nullable(Type.Integer())),
  delivered_total_items: Type.Optional(Nullable(Type.Integer())),
  received_total_items: Type.Optional(Nullable(Type.Integer())),
  overall_order_item_stock_status: Type.Optional(
    Type.Union([
      Type.Literal('on_stock'),
      Type.Literal('partially_out_of_stock'),
      Type.Null(),
    ])
  ),
  is_dummy: Type.Boolean(),
  ship_to_contact: Type.Union([
    Type.Omit(ShipToContact, ['id', 'created_at', 'updated_at']),
    Type.Null(),
  ]),
  delivery_option: Type.Union([
    Type.Literal('immediate'),
    Type.Literal('custom_date_2'),
    Type.Literal('custom_date_3'),
    Type.Literal('custom_date_4'),
    Type.Literal('custom_date_5'),
    Type.Null(),
  ]),
  delivery_date: Type.Union([Type.String(), Type.Null()]),
});

export const OrderMetaGenerateReport = Type.Object({
  id: Type.String({ format: 'uuid' }),
  order_meta_id: Type.String({ format: 'uuid' }),
  report_type: Type.String(),
  status: Type.Union([
    Type.Literal('pending'),
    Type.Literal('processing'),
    Type.Literal('success'),
    Type.Literal('failed_retryable'),
    Type.Literal('failed_final'),
  ]),
  s3_key: Nullable(Type.String()),
  s3_url: Nullable(Type.String()),
  file_name: Type.String(),
  attempt_count: Type.Integer({ minimum: 0 }),
  last_error_code: Nullable(Type.String()),
  last_error_message: Nullable(Type.String()),
  generated_at: Nullable(Type.Integer({ minimum: 0 })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const EmailNotification = Type.Object({
  id: Type.String({ format: 'uuid' }),
  topic: Type.String(),
  dedupe_key: Type.String(),
  reference_type: Nullable(Type.String()),
  reference_id: Nullable(Type.String()),
  metadata: Type.Record(Type.String(), Type.Unknown()),
  recipient: Type.String(),
  template: Type.String(),
  subject: Type.String(),
  payload: Type.Record(Type.String(), Type.Unknown()),
  status: Type.Union([
    Type.Literal('pending'),
    Type.Literal('queued'),
    Type.Literal('processing'),
    Type.Literal('sent'),
    Type.Literal('failed_final'),
  ]),
  provider_message_id: Nullable(Type.String()),
  last_error: Nullable(Type.String()),
  queued_at: Nullable(Type.Integer({ minimum: 0 })),
  sent_at: Nullable(Type.Integer({ minimum: 0 })),
  failed_at: Nullable(Type.Integer({ minimum: 0 })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const EmailAttempt = Type.Object({
  id: Type.String({ format: 'uuid' }),
  email_notification_id: Type.String({ format: 'uuid' }),
  attempt_number: Type.Integer({ minimum: 1 }),
  status: Type.Union([
    Type.Literal('processing'),
    Type.Literal('sent'),
    Type.Literal('failed'),
  ]),
  attempted_at: Type.Integer({ minimum: 0 }),
  completed_at: Nullable(Type.Integer({ minimum: 0 })),
  provider_message_id: Nullable(Type.String()),
  error_code: Nullable(Type.String()),
  error_message: Nullable(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const ShiptoRestockProfile = Type.Object({
  user_id: Type.String({ format: 'uuid' }),
  sap_no_shipto: Type.Integer(),
  last_real_order_at: Type.Integer({ minimum: 0 }),
  last_real_order_date: Type.String({ format: 'date' }),
  order_day_count: Type.Integer({ minimum: 1 }),
  usual_gap_days: Nullable(Type.Number()),
  due_day: Type.Integer({ minimum: 3, maximum: 14 }),
  next_reminder_date: Type.String({ format: 'date' }),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const ShiptoWelcomeCandidate = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  user_ship_to_id: Type.String({ format: 'uuid' }),
  sap_no_shipto: Type.Integer(),
  activation_date: Type.String({ format: 'date' }),
  welcome_due_date: Type.String({ format: 'date' }),
  status: Type.Union([
    Type.Literal('pending'),
    Type.Literal('reminder_created'),
    Type.Literal('suppressed'),
    Type.Literal('expired'),
  ]),
  reminder_id: Nullable(Type.String({ format: 'uuid' })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const ShiptoNotificationReminder = Type.Object({
  id: Type.String({ format: 'uuid' }),
  reminder_identity: Type.String(),
  reminder_type: Type.Union([
    Type.Literal('restock'),
    Type.Literal('cart'),
    Type.Literal('welcome'),
    Type.Literal('restock_4_weeks'),
  ]),
  status: Type.Union([Type.Literal('pending'), Type.Literal('queued')]),
  user_id: Type.String({ format: 'uuid' }),
  user_ship_to_id: Type.String({ format: 'uuid' }),
  sap_no_shipto: Type.Integer(),
  outlet_name: Type.String(),
  recipient_email: Type.String(),
  snapshot_at: Type.Integer({ minimum: 0 }),
  qualifying_event_at: Type.Integer({ minimum: 0 }),
  usual_gap_days: Nullable(Type.Number()),
  due_day: Nullable(Type.Integer({ minimum: 3, maximum: 14 })),
  message_title: Type.String(),
  message_body: Type.String(),
  queued_at: Nullable(Type.Integer({ minimum: 0 })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const ShiptoNotificationReminderProduct = Type.Object({
  reminder_id: Type.String({ format: 'uuid' }),
  product_id: Type.String({ format: 'uuid' }),
  created_at: Type.Integer({ minimum: 0 }),
});

export const BroadcastPushNotification = Type.Object({
  id: Type.String({ format: 'uuid' }),
  topic: Type.String({ pattern: '^lifecycle[.][a-z0-9][a-z0-9._-]*$' }),
  reminder_id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  payload: Type.Record(Type.String(), Type.Unknown()),
  status: Type.Union([
    Type.Literal('pending'),
    Type.Literal('queued'),
    Type.Literal('processing'),
    Type.Literal('sent'),
    Type.Literal('partial'),
    Type.Literal('no_targets'),
    Type.Literal('failed_final'),
    Type.Literal('cancelled'),
  ]),
  queued_at: Nullable(Type.Integer({ minimum: 0 })),
  processing_at: Nullable(Type.Integer({ minimum: 0 })),
  finished_at: Nullable(Type.Integer({ minimum: 0 })),
  last_error_code: Nullable(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const BroadcastPushNotificationDelivery = Type.Object({
  id: Type.String({ format: 'uuid' }),
  push_notification_id: Type.String({ format: 'uuid' }),
  mobile_session_id: Nullable(Type.String({ format: 'uuid' })),
  target_fingerprint: Type.String({
    minLength: 64,
    maxLength: 64,
    pattern: '^[0-9a-f]{64}$',
  }),
  status: Type.Union([
    Type.Literal('pending'),
    Type.Literal('processing'),
    Type.Literal('sent'),
    Type.Literal('invalid'),
    Type.Literal('failed_final'),
  ]),
  attempt_count: Type.Integer({ minimum: 0 }),
  provider_message_id: Nullable(Type.String()),
  last_error_code: Nullable(Type.String()),
  sent_at: Nullable(Type.Integer({ minimum: 0 })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const SalesOrder = Type.Object({
  id: Type.String({ format: 'uuid' }),
  so_number: Type.Integer(),
  so_created_at: Type.Union([Type.String(), Type.Null()]),
  sap_status: Type.Union([Type.String(), Type.Null()]),
  sap_delivery_status: Type.Union([Type.String(), Type.Null()]),
  sap_reject_status: Type.Union([Type.String(), Type.Null()]),
  cancel_reason: Type.Union([Type.String(), Type.Null()]),
});

export const SalesOrderItem = Type.Object({
  id: Type.String({ format: 'uuid' }),
  sales_order_id: Type.String({ format: 'uuid' }),
  line_item: Type.Integer(),
  item_code: Type.Integer(),
  quantity: Type.Integer(),
  net_value: Type.Integer(),
  cancel_reason: Type.Union([Type.String(), Type.Null()]),
});

export const Order = Type.Object({
  id: Type.String({ format: 'uuid' }),
  order_meta_id: Type.String({ format: 'uuid' }),
  status: Type.Union([
    Type.Literal('draft'),
    Type.Literal('submitted'),
    Type.Literal('waiting_for_payment'),
    Type.Literal('processed'),
    Type.Literal('partially_processed'),
    Type.Literal('out_for_delivery'),
    Type.Literal('partially_out_for_delivery'),
    Type.Literal('received'),
    Type.Literal('partially_received'),
    Type.Literal('cancelled'),
  ]),
  division: Type.Union([
    Type.Literal('diamond'),
    Type.Literal('trading'),
    Type.Literal('fnb'),
    Type.Literal('equipment'),
    Type.Literal('food'),
  ]),
  total_items: Type.Number(),
  original_subtotal: Type.Number(),
  subtotal: Type.Number(),
  saved_subtotal: Type.Number(),
  checkout_original_subtotal: Type.Optional(Nullable(Type.Integer())),
  checkout_subtotal: Type.Optional(Nullable(Type.Integer())),
  checkout_saved_subtotal: Type.Optional(Nullable(Type.Integer())),
  processed_original_subtotal: Type.Optional(Nullable(Type.Integer())),
  processed_subtotal: Type.Optional(Nullable(Type.Integer())),
  processed_saved_subtotal: Type.Optional(Nullable(Type.Integer())),
  delivered_original_subtotal: Type.Optional(Nullable(Type.Integer())),
  delivered_subtotal: Type.Optional(Nullable(Type.Integer())),
  delivered_saved_subtotal: Type.Optional(Nullable(Type.Integer())),
  received_original_subtotal: Type.Optional(Nullable(Type.Integer())),
  received_subtotal: Type.Optional(Nullable(Type.Integer())),
  received_saved_subtotal: Type.Optional(Nullable(Type.Integer())),
  latest_original_subtotal: Type.Optional(Nullable(Type.Integer())),
  latest_subtotal: Type.Optional(Nullable(Type.Integer())),
  latest_saved_subtotal: Type.Optional(Nullable(Type.Integer())),
  checkout_total_items: Type.Optional(Nullable(Type.Integer())),
  processed_total_items: Type.Optional(Nullable(Type.Integer())),
  delivered_total_items: Type.Optional(Nullable(Type.Integer())),
  received_total_items: Type.Optional(Nullable(Type.Integer())),
  external_id: Type.Optional(Type.String()),
  cancel_reason_id: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  note: Type.Optional(Nullable(Type.String())),
  invoice_link: Nullable(Type.String({ format: 'uri' })),
  delivery_link: Nullable(Type.String({ format: 'uri' })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Type.Integer({ minimum: 0 }),
  catalog_external_id: Type.Optional(Nullable(Type.String())),
  sub_po_number: Type.String(),
  so_number: Nullable(Type.String()),
  processed_at: Nullable(Type.Number()),
  sap_note: Type.Optional(Nullable(Type.String())),
  cg_1_business_segmentation: Type.String(),
  is_eligible_for_so_automation: Type.Boolean(),
  is_eligible_for_do_automation: Type.Optional(Type.Boolean()),
  is_submitted: Type.Boolean(),
  po_effective_date: Type.Optional(Type.Number()),
});

export const OrderItem = Type.Object({
  id: Type.String({ format: 'uuid' }),
  order_id: Type.String({ format: 'uuid' }),
  product_id: Type.String({ format: 'uuid' }),
  quantity: Type.Optional(Type.Integer({ exclusiveMinimum: 0 })),
  price: Type.Optional(Type.Number()),
  original_subtotal: Type.Number(),
  subtotal: Type.Number(),
  saved_subtotal: Type.Number(),
  checkout_quantity: Type.Optional(Nullable(Type.Integer())),
  checkout_original_subtotal: Type.Optional(Nullable(Type.Integer())),
  checkout_subtotal: Type.Optional(Nullable(Type.Integer())),
  checkout_saved_subtotal: Type.Optional(Nullable(Type.Integer())),
  checkout_original_unit_price: Type.Optional(Nullable(Type.Integer())),
  checkout_unit_price: Type.Optional(Nullable(Type.Integer())),
  processed_original_subtotal: Type.Optional(Nullable(Type.Integer())),
  processed_saved_subtotal: Type.Optional(Nullable(Type.Integer())),
  processed_original_unit_price: Type.Optional(Nullable(Type.Integer())),
  processed_unit_price: Type.Optional(Nullable(Type.Integer())),
  delivered_original_subtotal: Type.Optional(Nullable(Type.Integer())),
  delivered_saved_subtotal: Type.Optional(Nullable(Type.Integer())),
  delivered_original_unit_price: Type.Optional(Nullable(Type.Integer())),
  delivered_unit_price: Type.Optional(Nullable(Type.Integer())),
  received_original_subtotal: Type.Optional(Nullable(Type.Integer())),
  received_saved_subtotal: Type.Optional(Nullable(Type.Integer())),
  received_original_unit_price: Type.Optional(Nullable(Type.Integer())),
  received_unit_price: Type.Optional(Nullable(Type.Integer())),
  latest_quantity: Type.Optional(Nullable(Type.Integer())),
  latest_original_subtotal: Type.Optional(Nullable(Type.Integer())),
  latest_subtotal: Type.Optional(Nullable(Type.Integer())),
  latest_saved_subtotal: Type.Optional(Nullable(Type.Integer())),
  latest_original_unit_price: Type.Optional(Nullable(Type.Integer())),
  latest_unit_price: Type.Optional(Nullable(Type.Integer())),
  note: Type.Optional(Nullable(Type.String())),
  valuation_type: Type.Optional(Nullable(Type.String())),
  price_source: Type.Optional(
    Type.Union([Type.Literal('PLU'), Type.Literal('PLK'), Type.Null()])
  ),
  is_updated: Type.Optional(Type.Boolean()),
  update_origin: Type.Union([
    Type.Literal('admin'),
    Type.Literal('sap'),
    Type.Null(),
  ]),
  is_not_processed: Type.Optional(Type.Boolean()),
  confirmed_quantity: Type.Optional(Type.Integer({ minimum: 0 })),
  latest_price: Type.Optional(Type.Number(Type.Integer({ minimum: 0 }))),
  product_storage: Type.Optional(
    Type.Union([
      Type.Literal('frozen'),
      Type.Literal('dry'),
      Type.Literal('dry_ac'),
      Type.Literal('chilled'),
      Type.Null(),
    ])
  ),
  cancel_reason_id: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  processed_quantity: Type.Optional(Nullable(Type.Integer())),
  delivered_quantity: Type.Optional(Nullable(Type.Integer())),
  received_quantity: Type.Optional(Nullable(Type.Integer())),
  processed_subtotal: Type.Optional(Nullable(Type.Integer())),
  delivered_subtotal: Type.Optional(Nullable(Type.Integer())),
  received_subtotal: Type.Optional(Nullable(Type.Integer())),
  delivery_order_number: Type.Optional(Nullable(Type.String())),
  is_price_diff: Type.Optional(Type.Boolean()),
  is_stock_diff: Type.Optional(Type.Boolean()),
  price_diff: Type.Optional(Type.Number()),
  stock_diff: Type.Optional(Type.Number()),
  line_item: Type.Integer({ multipleOf: 10 }),
  line_item_sap: Type.String(),
  original_price: Type.Optional(Type.Number()),
  original_stock: Type.Optional(Type.Integer()),
  order_type: Nullable(Type.String()),
  is_new_product: Type.Boolean(),
});

export const DeliveryOrder = Type.Object({
  id: Type.String({ format: 'uuid' }),
  do_number: Nullable(Type.String()),
  order_id: Type.String({ format: 'uuid' }),
  plant_id: Nullable(Type.String()),
  plant_name: Nullable(Type.String()),
  status: Type.Union([
    Type.Literal('processed'),
    Type.Literal('delivered'),
    Type.Literal('received'),
    Type.Literal('rejected'),
    Type.Literal('partially_received'),
    Type.Literal('cancelled'),
  ]),
  is_recreated: Type.Optional(Type.Boolean()),
  do_processed_at: Nullable(Type.String({ format: 'date' })),
  do_delivered_at: Nullable(Type.String({ format: 'date' })),
  do_received_at: Nullable(Type.String({ format: 'date' })),
  do_rejected_at: Nullable(Type.String({ format: 'date' })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Type.Integer({ minimum: 0 }),
});

export const DeliveryOrderItem = Type.Object({
  id: Type.String({ format: 'uuid' }),
  delivery_order_id: Type.String({ format: 'uuid' }),
  order_item_id: Type.String({ format: 'uuid' }),
  item_code: Type.Integer(),
  delivered_quantity: Nullable(Type.Integer()),
  received_quantity: Type.Optional(Nullable(Type.Integer())),
  pod_reason: Nullable(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Type.Integer({ minimum: 0 }),
});

export const DeliveryOrderHistory = Type.Object({
  id: Type.String({ format: 'uuid' }),
  delivery_order_id: Type.String({ format: 'uuid' }),
  status: Type.Union([
    Type.Literal('processed'),
    Type.Literal('delivered'),
    Type.Literal('received'),
    Type.Literal('rejected'),
    Type.Literal('partially_received'),
    Type.Literal('cancelled'),
  ]),
  created_at: Type.Integer({ minimum: 0 }),
});

export const OrderWebhook = Type.Object({
  id: Type.String({ format: 'uuid' }),
  header: Type.Record(Type.String(), Type.Unknown()),
  body: Type.Record(Type.String(), Type.Unknown()),
  created_at: Type.Integer({ minimum: 0 }),
});

export const OrderWebhookLog = Type.Object({
  id: Type.String({ format: 'uuid' }),
  so_number: Type.Integer(),
  sap_status: Type.String(),
  sap_delivery_status: Type.String(),
  sap_reject_status: Type.String(),
  cancel_reason: Nullable(Type.String()),
  billing_status: Nullable(Type.String()),
  customer_name: Nullable(Type.String()),
  address: Nullable(Type.String()),
  pgi_date: Nullable(Type.String()),
  pgi_time: Nullable(Type.String()),
  picking_date: Nullable(Type.String()),
  picking_time: Nullable(Type.String()),
  pod_date: Nullable(Type.String()),
  pod_time: Nullable(Type.String()),
  delivery_number: Nullable(Type.Integer()),
  delivery_date: Nullable(Type.String()),
  delivery_link: Nullable(Type.String()),
  shipment_status: Nullable(Type.String()),
  shipment_no: Nullable(Type.Integer()),
  shipment_date: Nullable(Type.String()),
  shipment_time: Nullable(Type.String()),
  header_raw: Type.Record(Type.String(), Type.Unknown()),
  body_raw: Type.Record(Type.String(), Type.Unknown()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Type.Integer({ minimum: 0 }),
});

export const OrderWebhookDetailLog = Type.Object({
  id: Type.String({ format: 'uuid' }),
  order_webhook_log_id: Type.String({ format: 'uuid' }),
  qty_pgi: Nullable(Type.Integer()),
  qty_pod: Nullable(Type.Integer()),
  material: Nullable(Type.Integer()),
  so_lines: Nullable(Type.Integer()),
  sales_unit: Nullable(Type.String()),
  delivery_qty: Nullable(Type.Integer()),
  delivery_lines: Nullable(Type.Integer()),
  delivery_number: Nullable(Type.Integer()),
  cancel_reason: Nullable(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Type.Integer({ minimum: 0 }),
});

export const DeliveryOrderWebhookLog = Type.Object({
  id: Type.String({ format: 'uuid' }),
  so_number: Nullable(Type.String()),
  delivery_number: Nullable(Type.String()),
  deletion_flag: Nullable(Type.String()),
  detail_count: Type.Integer({ minimum: 0 }),
  header_raw: Type.Record(Type.String(), Type.Unknown()),
  body_raw: Type.Record(Type.String(), Type.Unknown()),
  created_at: Type.Integer({ minimum: 0 }),
});

export const DeliveryOrderWebhookDetailLog = Type.Object({
  id: Type.String({ format: 'uuid' }),
  log_id: Type.String({ format: 'uuid' }),
  material_id: Nullable(Type.String()),
  delivery_qty: Nullable(Type.String()),
  qty_pgi: Nullable(Type.String()),
  qty_pod: Nullable(Type.String()),
  sales_unit: Nullable(Type.String()),
  pod_reason: Nullable(Type.String()),
});

export const SalesOrderWebhookLog = Type.Object({
  id: Type.String({ format: 'uuid' }),
  so_number: Type.Integer(),
  sap_status: Type.String(),
  sap_delivery_status: Type.String(),
  sap_reject_status: Type.String(),
  cancel_reason: Nullable(Type.String()),
  req_deliv_date: Nullable(Type.String()),
  created_by: Nullable(Type.String()),
  so_created_date: Nullable(Type.String()),
  so_created_time: Nullable(Type.String()),
  header_raw: Type.Record(Type.String(), Type.Unknown()),
  body_raw: Type.Record(Type.String(), Type.Unknown()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Type.Integer({ minimum: 0 }),
});

export const SalesOrderWebhookDetailLog = Type.Object({
  id: Type.String({ format: 'uuid' }),
  log_id: Type.String({ format: 'uuid' }),
  line_item: Nullable(Type.Integer()),
  material: Nullable(Type.Integer()),
  so_qty: Nullable(Type.Number()),
  net_value: Nullable(Type.Number()),
  cancel_reason: Nullable(Type.String()),
  valuation_type: Nullable(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Type.Integer({ minimum: 0 }),
});

export const BillingWebhookLog = Type.Object({
  id: Type.String({ format: 'uuid' }),
  billing_number: Nullable(Type.String()),
  sales_order_number: Nullable(Type.String()),
  delivery_number: Nullable(Type.String()),
  detail_count: Nullable(Type.Integer()),
  header_raw: Nullable(Type.Record(Type.String(), Type.Unknown())),
  body_raw: Nullable(Type.Record(Type.String(), Type.Unknown())),
  created_at: Type.Integer({ minimum: 0 }),
});

export const BillingWebhookDetailLog = Type.Object({
  id: Type.String({ format: 'uuid' }),
  log_id: Type.String({ format: 'uuid' }),
  billing_item: Nullable(Type.String()),
  product_item_code: Nullable(Type.String()),
  qty: Nullable(Type.Integer()),
  uom: Nullable(Type.String()),
  value: Nullable(Type.Integer()),
  initial_price: Nullable(Type.Integer()),
});

// ---------------------------------------------------------------------------
// Order Payment
// ---------------------------------------------------------------------------
export const OrderPayment = Type.Object({
  id: Type.String({ format: 'uuid' }),
  order_meta_id: Type.String({ format: 'uuid' }),
  bank_name: Nullable(Type.String()),
  bank_account: Nullable(Type.String()),
  expired_at: Type.Integer({ minimum: 0 }),
  total: Type.Integer({ minimum: 0 }),
});

export const OrderPaymentProof = Type.Object({
  id: Type.String({ format: 'uuid' }),
  order_payment_id: Type.String({ format: 'uuid' }),
  url: Type.String({ format: 'uri' }),
  created_at: Type.Integer({ minimum: 0 }),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  updated_at: Type.Integer({ minimum: 0 }),
  updated_by: Nullable(Type.String({ format: 'uuid' })),
});

export const OrderPaymentProofOcrResult = Type.Object({
  id: Type.String({ format: 'uuid' }),
  order_payment_id: Type.String({ format: 'uuid' }),
  order_payment_proof_id: Nullable(Type.String({ format: 'uuid' })),
  url: Type.String(),
  original_file_name: Type.String(),
  normalized_file_name: Nullable(Type.String()),
  mime_type: Type.String(),
  file_size: Type.Integer({
    minimum: 0,
    description: 'File size in bytes',
  }),
  status: Type.String(),
  sender_bank: Nullable(Type.String()),
  manual_sender_bank: Nullable(Type.String()),
  manual_sender_account_name: Nullable(Type.String()),
  recipient_name: Nullable(Type.String()),
  manual_recipient_name: Nullable(Type.String()),
  recipient_account_number: Nullable(Type.String()),
  amount: Nullable(Type.Integer({ minimum: 0 })),
  manual_amount: Nullable(Type.Integer({ minimum: 0 })),
  transaction_note: Nullable(Type.String()),
  reference_number: Nullable(Type.String()),
  reference_key: Nullable(Type.String()),
  transaction_datetime: Nullable(Type.Integer({ minimum: 0 })),
  manual_transaction_datetime: Nullable(Type.Integer({ minimum: 0 })),
  confidence_score: Nullable(Type.Number()),
  raw_ocr_response: Nullable(Type.Record(Type.String(), Type.Unknown())),
  attempt_count: Type.Integer({ minimum: 0 }),
  last_error_code: Nullable(Type.String()),
  last_error_message: Nullable(Type.String()),
  external_request_id: Nullable(Type.String()),
  manual_input_reason: Nullable(Type.String()),
  manual_input_at: Nullable(Type.Integer({ minimum: 0 })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const OrderPoDocs = Type.Object({
  id: Type.String({ format: 'uuid' }),
  order_meta_id: Type.String({ format: 'uuid' }),
  url: Type.String({ format: 'uri' }),
});
// ---------------------------------------------------------------------------
// Order Status History
// ---------------------------------------------------------------------------
export const OrderStatusHistory = Type.Object({
  id: Type.String({ format: 'uuid' }),
  order_id: Type.String({ format: 'uuid' }),
  status: Type.String(),
  note: Nullable(Type.String({ nullable: true })),
  origin: Type.Union([
    Type.Literal('buyer'),
    Type.Literal('admin'),
    Type.Literal('sap'),
    Type.Literal('sol'),
    Type.Literal('so_automation'),
    Type.Null(),
  ]),
  client_type: Type.Optional(
    Type.Union([
      Type.Literal('android'),
      Type.Literal('iphone'),
      Type.Literal('web'),
      Type.Null(),
    ])
  ),
  client_os: Type.Optional(
    Type.Union([
      Type.Literal('windows'),
      Type.Literal('linux'),
      Type.Literal('macos'),
      Type.Literal('android'),
      Type.Literal('ios'),
      Type.Null(),
    ])
  ),
  client_device: Type.Optional(Type.String()),
  client_version: Type.Optional(Type.String()),
  created_by: Type.Optional(Type.String({ format: 'uuid' })),
  created_at: Type.Integer({ minimum: 0 }),
});

// ---------------------------------------------------------------------------
// Order Changelog
// ---------------------------------------------------------------------------

export const OrderChangelog = Type.Object({
  id: Type.String({ format: 'uuid' }),
  order_id: Type.String(),
  changed_field: Type.Union([
    Type.Literal('confirmed_quantity'),
    Type.Literal('latest_price'),
  ]),
  origin: Type.Union([Type.Literal('admin'), Type.Literal('sap')]),
  product_item_code: Type.Integer(),
  value_before: Type.Integer(),
  value_after: Type.Integer(),
  description: Type.String(),
  created_at: Type.Integer({ exclusiveMinimum: 0 }),
  created_by: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
});

// ---------------------------------------------------------------------------
// Cart
// ---------------------------------------------------------------------------
export const Cart = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  product_id: Type.String({ format: 'uuid' }),
  warehouse_id: Type.String({ format: 'uuid' }),
  quantity: Type.Integer({ exclusiveMinimum: 0 }),
  note: Nullable(Type.String({ minLength: 0 })),
  is_deleted: Type.Optional(Type.Boolean({ default: false })),
});

export const CartRecommendationScoringConfigSet = Type.Object({
  id: Type.String({ format: 'uuid' }),
  config_name: Type.String(),
  formula_version: Type.String(),
  is_active: Type.Boolean(),
  description: Nullable(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const CartRecommendationConfigValueType = Type.Union([
  Type.Literal('number'),
  Type.Literal('boolean'),
  Type.Literal('text'),
]);

export const CartRecommendationScoringConfigValue = Type.Object({
  id: Type.String({ format: 'uuid' }),
  config_set_id: Type.String({ format: 'uuid' }),
  config_group: Type.String(),
  config_key: Type.String(),
  value_type: CartRecommendationConfigValueType,
  number_value: Nullable(Type.Number()),
  boolean_value: Nullable(Type.Boolean()),
  text_value: Nullable(Type.String()),
  description: Nullable(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const CartRecommendationGenerationRunStatus = Type.Union([
  Type.Literal('running'),
  Type.Literal('completed'),
  Type.Literal('failed'),
  Type.Literal('abandoned'),
  Type.Literal('superseded'),
]);

export const CartRecommendationGenerationRun = Type.Object({
  id: Type.String({ format: 'uuid' }),
  scoring_config_set_id: Nullable(Type.String({ format: 'uuid' })),
  analysis_start_at: Type.Integer({ minimum: 0 }),
  analysis_end_at: Type.Integer({ minimum: 0 }),
  formula_version: Type.String(),
  status: CartRecommendationGenerationRunStatus,
  parameters: Type.Record(Type.String(), Type.Unknown()),
  product_cart_count: Type.Integer({ minimum: 0 }),
  candidate_count: Type.Integer({ minimum: 0 }),
  recommendation_product_count: Type.Integer({ minimum: 0 }),
  error_message: Nullable(Type.String()),
  started_at: Type.Integer({ minimum: 0 }),
  completed_at: Nullable(Type.Integer({ minimum: 0 })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const CartRecommendationStepLogStatus = Type.Union([
  Type.Literal('pending'),
  Type.Literal('running'),
  Type.Literal('completed'),
  Type.Literal('skipped'),
  Type.Literal('failed'),
]);

export const CartRecommendationStepLog = Type.Object({
  id: Type.String({ format: 'uuid' }),
  generation_run_id: Type.String({ format: 'uuid' }),
  step_name: Type.String(),
  status: CartRecommendationStepLogStatus,
  attempt_number: Type.Integer({ minimum: 1 }),
  inserted_count: Type.Integer({ minimum: 0 }),
  skipped_count: Type.Integer({ minimum: 0 }),
  error_code: Nullable(Type.String()),
  error_message: Nullable(Type.String()),
  metadata: Nullable(Type.Record(Type.String(), Type.Unknown())),
  started_at: Type.Integer({ minimum: 0 }),
  completed_at: Nullable(Type.Integer({ minimum: 0 })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const CartRecommendationProductCart = Type.Object({
  id: Type.String({ format: 'uuid' }),
  generation_run_id: Nullable(Type.String({ format: 'uuid' })),
  product_cart_product_id: Nullable(Type.String({ format: 'uuid' })),
  product_cart_item_code: Type.Integer({ minimum: 1 }),
  cg3_business_type: Nullable(Type.String()),
  cg3_business_type_desc: Nullable(Type.String()),
  product_cart_order_count: Nullable(Type.Integer({ minimum: 0 })),
  recommendation_count: Nullable(Type.Integer({ minimum: 0 })),
  is_active: Type.Boolean(),
  generated_at: Type.Integer({ minimum: 0 }),
  expires_at: Nullable(Type.Integer({ minimum: 0 })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const CartRecommendationSource = Type.Union([
  Type.Literal('co_purchase_pair'),
  Type.Literal('shipto_history'),
  Type.Literal('segment_top_seller'),
]);

export const CartRecommendationTier = Type.Union([
  Type.Literal('strong_pair'),
  Type.Literal('shipto_history'),
  Type.Literal('segment_fallback'),
]);

export const CartRecommendationProductCandidate = Type.Object({
  id: Type.String({ format: 'uuid' }),
  generation_run_id: Nullable(Type.String({ format: 'uuid' })),
  product_cart_id: Type.String({ format: 'uuid' }),
  product_cart_item_code: Type.Integer({ minimum: 1 }),
  recommended_product_id: Nullable(Type.String({ format: 'uuid' })),
  recommended_product_item_code: Type.Integer({ minimum: 1 }),
  recommendation_source: CartRecommendationSource,
  recommendation_tier: CartRecommendationTier,
  source_score: Type.Number(),
  pair_orders: Nullable(Type.Integer({ minimum: 0 })),
  product_cart_orders: Nullable(Type.Integer({ minimum: 0 })),
  recommended_orders: Nullable(Type.Integer({ minimum: 0 })),
  segment_order_count: Nullable(Type.Integer({ minimum: 0 })),
  confidence_pct: Nullable(Type.Number()),
  support_pct: Nullable(Type.Number()),
  lift: Nullable(Type.Number()),
  is_weak_evidence: Type.Boolean(),
  selected_for_final: Type.Boolean(),
  generated_at: Type.Integer({ minimum: 0 }),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const CartRecommendationProduct = Type.Object({
  id: Type.String({ format: 'uuid' }),
  generation_run_id: Nullable(Type.String({ format: 'uuid' })),
  product_cart_id: Type.String({ format: 'uuid' }),
  product_cart_item_code: Type.Integer({ minimum: 1 }),
  recommended_product_id: Nullable(Type.String({ format: 'uuid' })),
  recommended_product_item_code: Type.Integer({ minimum: 1 }),
  recommendation_source: CartRecommendationSource,
  recommendation_tier: CartRecommendationTier,
  recommendation_rank: Type.Integer({ minimum: 1 }),
  pair_orders: Nullable(Type.Integer({ minimum: 0 })),
  product_cart_orders: Nullable(Type.Integer({ minimum: 0 })),
  recommended_orders: Nullable(Type.Integer({ minimum: 0 })),
  segment_order_count: Nullable(Type.Integer({ minimum: 0 })),
  confidence_pct: Nullable(Type.Number()),
  support_pct: Nullable(Type.Number()),
  lift: Nullable(Type.Number()),
  final_score: Type.Number(),
  is_weak_evidence: Type.Boolean(),
  is_active: Type.Boolean(),
  generated_at: Type.Integer({ minimum: 0 }),
  expires_at: Nullable(Type.Integer({ minimum: 0 })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

// ---------------------------------------------------------------------------
// Order Template
// ---------------------------------------------------------------------------
export const OrderTemplate = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  name: Type.String(),
  note: Nullable(Type.String()),
  warehouse_id: Type.String({ format: 'uuid' }),
  created_by: Type.Optional(Type.String({ format: 'uuid' })),
});

export const OrderTemplateItem = Type.Object({
  id: Type.String({ format: 'uuid' }),
  order_template_id: Type.String({ format: 'uuid' }),
  product_id: Type.String(),
  quantity: Type.Integer({ exclusiveMinimum: 0 }),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

// ---------------------------------------------------------------------------
// Order Minimum Spend
// ---------------------------------------------------------------------------
export const MinimumSpend = Type.Object({
  id: Type.String({ format: 'uuid' }),
  branch: Type.String(),
  distribution_channel: Type.String(),
  cg_4_manufacture: Type.String(),
  cg_5_trading: Type.String(),
  city_position: Type.String(),
  minimum_order: Type.Number(),
  key: Type.String(),
  url: Type.String(),
  bank_name: Type.String(),
  bank_account_number: Type.String(),
});

// ---------------------------------------------------------------------------
// Wishlist
// ---------------------------------------------------------------------------
export const Wishlist = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  product_id: Type.String({ format: 'uuid' }),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Type.Integer({ minimum: 0 }),
  warehouse_id: Type.String({ format: 'uuid' }),
});

// ---------------------------------------------------------------------------
// Banner
// ---------------------------------------------------------------------------
export const Banner = Type.Object({
  id: Type.String({ format: 'uuid' }),
  title: Type.String(),
  image: Type.String(),
  filename: Type.String(),
  url: Type.Optional(Nullable(Type.String({ format: 'uri' }))),
  index: Type.Integer(),
  business_segmentation: Type.Array(Type.String()),
  distribution_channel: Type.Array(Type.String()),
  promo_id: Type.Optional(Nullable(Type.String({ format: 'uuid' }))),
  shipto_csv_url: Type.Optional(Nullable(Type.String())),
  banner_type: Type.Optional(
    Type.Union(
      [Type.Literal('general'), Type.Literal('specific'), Type.Null()],
      { default: 'general' }
    )
  ),
  created_at: Type.Integer({ minimum: 0 }),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  updated_at: Type.Integer({ minimum: 0 }),
  updated_by: Nullable(Type.String({ format: 'uuid' })),
  deleted_at: Type.Integer({ minimum: 0 }),
  deleted_by: Nullable(Type.String({ format: 'uuid' })),
});

// ---------------------------------------------------------------------------
// Banner Shipto
// ---------------------------------------------------------------------------
export const BannerShipto = Type.Object({
  id: Type.String({ format: 'uuid' }),
  banner_id: Type.String({ format: 'uuid' }),
  sap_no_shipto: Type.Number(),
  created_at: Type.Integer({ minimum: 0 }),
});

// ---------------------------------------------------------------------------
// Banner Business Type
// ---------------------------------------------------------------------------
export const BannerBusinessType = Type.Object({
  id: Type.String({ format: 'uuid' }),
  banner_id: Type.String({ format: 'uuid' }),
  cg_3_business_type: Type.String(),
  cg_3_business_type_desc: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
});

// ---------------------------------------------------------------------------
// Audit Log
// ---------------------------------------------------------------------------
export const AuditLog = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  topic: Type.Union([
    Type.Literal('order'),
    Type.Literal('user'),
    Type.Literal('product'),
  ]),
  reference_id: Type.String({ format: 'uuid' }),
  user_agent: Type.String(),
  ip_address: Type.String(),
  operation_code: Type.Union([
    Type.Literal('insert'),
    Type.Literal('update'),
    Type.Literal('no_update'),
    Type.Literal('delete'),
  ]),
  operation_description: Type.String(),
  data_before: Type.Any(),
  data_after: Type.Any(),
  created_at: Type.Integer({ minimum: 0 }),
});

export const WebPortalLog = Type.Object({
  id: Type.String({ format: 'uuid' }),
  webportal_response: Type.Any(),
  data: Type.Any(),
  topic: Nullable(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const BankAccount = Type.Object({
  id: Type.String({ format: 'uuid' }),
  branch: Type.String(),
  distribution_channel: Type.String(),
  bank_name: Type.String(),
  bank_account_number: Type.String(),
});

// ---------------------------------------------------------------------------
// Admin Access
// ---------------------------------------------------------------------------
export const AdminAccess = Type.Object({
  id: Type.String({ format: 'uuid' }),
  email: Type.String({ format: 'email' }),
  distribution_channel: Type.String(),
  division: Type.String(),
  ship_to: Type.String(),
});

// ---------------------------------------------------------------------------
// Brands
// ---------------------------------------------------------------------------
export const Brand = Type.Object({
  id: Type.String({ format: 'uuid' }),
  brand_name: Type.String(),
  brand_slug: Type.String(),
  brand_desc: Nullable(Type.String()),
  desc: Nullable(Type.String()),
  logo_url: Nullable(Type.String()),
  cover_url: Nullable(Type.String()),
  country_name: Nullable(Type.String()),
  country_code: Nullable(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

// ---------------------------------------------------------------------------
// Certifications
// ---------------------------------------------------------------------------
export const Certification = Type.Object({
  id: Type.String({ format: 'uuid' }),
  certification_name: Type.String(),
  certification_slug: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

// ---------------------------------------------------------------------------
// Brand Certifications
// ---------------------------------------------------------------------------
export const BrandCertification = Type.Object({
  id: Type.String({ format: 'uuid' }),
  certification_id: Type.String({ format: 'uuid' }),
  brand_id: Type.String({ format: 'uuid' }),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------
export const Category = Type.Object({
  id: Type.String({ format: 'uuid' }),
  category_name: Type.String(),
  category_slug: Type.String(),
  category_parent_id: Nullable(Type.String({ format: 'uuid' })),
  sap_category_id: Nullable(Type.String()),
  sap_sub_category_id: Nullable(Type.String()),
  sap_sub_third_category_id: Nullable(Type.String()),
  index: Type.Integer(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

// ---------------------------------------------------------------------------
// Brand Categories
// ---------------------------------------------------------------------------
export const BrandCategory = Type.Object({
  id: Type.String({ format: 'uuid' }),
  brand_id: Type.String({ format: 'uuid' }),
  category_id: Type.String({ format: 'uuid' }),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

// ---------------------------------------------------------------------------
// Brand Highlights
// ---------------------------------------------------------------------------
export const BrandHighlights = Type.Object({
  id: Type.String({ format: 'uuid' }),
  brand_id: Type.String({ format: 'uuid' }),
  index: Type.Integer(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

// ---------------------------------------------------------------------------
// Trend Insights
// ---------------------------------------------------------------------------
export const TrendItem = Type.Object({
  id: Type.String({ format: 'uuid' }),
  slug: Type.String(),
  keyword: Type.String(),
  category: Type.Union([Type.Literal('food'), Type.Literal('beverage')]),
  sub_category: Nullable(Type.String()),
  is_top_trending: Nullable(Type.Boolean()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const TrendItemMetric30Days = Type.Object({
  id: Type.String({ format: 'uuid' }),
  trend_item_id: Type.String({ format: 'uuid' }),
  area: Nullable(Type.String()),
  post_count: Type.Integer({ minimum: 0 }),
  play_count: Type.Integer({ minimum: 0 }),
  likes_count: Type.Integer({ minimum: 0 }),
  comments_count: Type.Integer({ minimum: 0 }),
  shares_count: Type.Integer({ minimum: 0 }),
  outlet_count: Nullable(Type.Integer({ minimum: 0 })),
  collect_count: Type.Integer({ minimum: 0 }),
  trend_score: Type.Number({ minimum: 0, maximum: 99.99, multipleOf: 0.01 }),
  analysis_desc: Nullable(Type.String()),
  post_count_change_pct: Nullable(Type.Number()),
  play_count_change_pct: Nullable(Type.Number()),
  likes_count_change_pct: Nullable(Type.Number()),
  comments_count_change_pct: Nullable(Type.Number()),
  shares_count_change_pct: Nullable(Type.Number()),
  trend_score_change_pct: Nullable(Type.Number()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const TrendItemRecipe = Type.Object({
  id: Type.String({ format: 'uuid' }),
  trend_item_id: Type.String({ format: 'uuid' }),
  recipe_id: Type.String({ format: 'uuid' }),
  area: Nullable(Type.String()),
  competition_category: Type.Union([
    Type.Literal('low'),
    Type.Literal('medium'),
    Type.Literal('high'),
  ]),
  cogs_price: Nullable(Type.Number({ multipleOf: 0.01 })),
  range_price_min: Nullable(Type.Number({ multipleOf: 0.01 })),
  range_price_max: Nullable(Type.Number({ multipleOf: 0.01 })),
  min_margin: Nullable(Type.Number({ multipleOf: 0.01 })),
  max_margin: Nullable(Type.Number({ multipleOf: 0.01 })),
  index: Nullable(Type.Integer()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const TrendItemProduct = Type.Object({
  id: Type.String({ format: 'uuid' }),
  trend_item_id: Type.String({ format: 'uuid' }),
  product_id: Type.String({ format: 'uuid' }),
  index: Nullable(Type.Integer()),
  created_at: Type.Integer({ minimum: 0 }),
});

export const TrendItemBrand = Type.Object({
  id: Type.String({ format: 'uuid' }),
  trend_item_id: Type.String({ format: 'uuid' }),
  brand_id: Type.String({ format: 'uuid' }),
  index: Nullable(Type.Integer()),
  created_at: Type.Integer({ minimum: 0 }),
});

export const TrendItemTiktokVideo = Type.Object({
  id: Type.String({ format: 'uuid' }),
  trend_item_id: Type.String({ format: 'uuid' }),
  tiktok_video_url: Type.String(),
  author_username: Nullable(Type.String()),
  posted_at: Nullable(Type.Integer({ minimum: 0 })),
  index: Nullable(Type.Integer()),
  play_count: Type.Integer({ minimum: 0 }),
  likes_count: Type.Integer({ minimum: 0 }),
  comments_count: Type.Integer({ minimum: 0 }),
  shares_count: Type.Integer({ minimum: 0 }),
  collect_count: Type.Integer({ minimum: 0 }),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const Tag = Type.Object({
  id: Type.String({ format: 'uuid' }),
  name: Type.String(),
  slug: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
});

export const TrendItemTag = Type.Object({
  trend_item_id: Type.String({ format: 'uuid' }),
  tag_id: Type.String({ format: 'uuid' }),
});

// Misc
export const WebportalToken = Type.Object({
  id: Type.String(),
  token: Type.String(),
  base_url: Type.String(),
  expires_in: Type.Number(),
});

// Cancel Reason
export const CancelReason = Type.Object({
  id: Type.String({ format: 'uuid' }),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  reason_id: Type.String(),
  reason: Type.String(),
  reason_type: Type.String(),
  reason_status: Type.String(),
});

// Tokens
export const Tokens = Type.Object({
  id: Type.String({ format: 'uuid' }),
  email: Type.String(),
  token: Type.String(),
  event: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Type.Integer({ minimum: 0 }),
});

export const OrderStatusMappingConfig = Type.Object({
  key: Type.String(),
  rules: Type.Record(Type.String(), Type.Unknown()),
  description: Type.String(),
  updated_at: Type.Integer({ minimum: 0 }),
});

// Notifications
export const Notification = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  shipto_id: Type.String({ format: 'uuid' }),
  title: Type.String(),
  body: Type.String(),
  metadata: Type.Record(Type.String(), Type.Unknown()),
  is_read: Type.Boolean(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Type.Integer({ minimum: 0 }),
});

// Last login
export const UserLastLogin = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  last_login: Nullable(Type.String()),
});

// Promos
export const DiscountPromo = Type.Object({
  order_type: Type.String(),
  sap_no_shipto: Nullable(Type.Number()),
  sales_org_shipto: Type.String(),
  distribution_channel: Nullable(Type.String()),
  cg_1_business_segmentation: Nullable(Type.String()),
  cg_2_business_category: Nullable(Type.String()),
  cg_3_business_type: Nullable(Type.String()),
  cg_4_manufacture: Nullable(Type.String()),
  attribute_1: Nullable(Type.String()),
  attribute_2: Nullable(Type.String()),
  attribute_8: Nullable(Type.String()),
  attribute_9: Nullable(Type.String()),
  product_brand_name: Nullable(Type.String()),
  product_type: Nullable(Type.String()),
  product_item_code: Nullable(Type.Integer()),
  minimum_order_quantity: Type.Integer({ minimum: 0 }),
  minimum_order_amount: Type.Integer({ minimum: 0 }),
  sum_minimum_order_quantity: Type.Integer({ minimum: 0 }),
  sum_minimum_order_amount: Type.Integer({ minimum: 0 }),
  condition_type: Nullable(Type.String()),
  calculation_number: Type.Integer(),
  deduction_amount: Type.Integer(),
  is_discount: Type.Boolean(),
  condition_pricing_unit: Nullable(Type.String()),
  condition_unit: Nullable(Type.String()),
  valid_from: Type.Integer(),
  valid_until: Type.Integer(),
  sap_created_at: Type.String(),
  prorate: Type.Boolean(),
  promo_code: Type.String(),
  is_from_sap: Type.Boolean(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const DiscountPromoInactive = Type.Object({
  order_type: Type.String(),
  sap_no_shipto: Nullable(Type.Number()),
  sales_org_shipto: Type.String(),
  distribution_channel: Nullable(Type.String()),
  cg_1_business_segmentation: Nullable(Type.String()),
  cg_2_business_category: Nullable(Type.String()),
  cg_3_business_type: Nullable(Type.String()),
  cg_4_manufacture: Nullable(Type.String()),
  attribute_1: Nullable(Type.String()),
  attribute_2: Nullable(Type.String()),
  attribute_8: Nullable(Type.String()),
  attribute_9: Nullable(Type.String()),
  product_brand_name: Nullable(Type.String()),
  product_type: Nullable(Type.String()),
  product_item_code: Nullable(Type.Integer()),
  minimum_order_quantity: Type.Integer({ minimum: 0 }),
  minimum_order_amount: Type.Integer({ minimum: 0 }),
  sum_minimum_order_quantity: Type.Integer({ minimum: 0 }),
  sum_minimum_order_amount: Type.Integer({ minimum: 0 }),
  condition_type: Nullable(Type.String()),
  calculation_number: Type.Integer(),
  deduction_amount: Type.Integer(),
  is_discount: Type.Boolean(),
  condition_pricing_unit: Nullable(Type.String()),
  condition_unit: Nullable(Type.String()),
  valid_from: Type.Integer(),
  valid_until: Type.Integer(),
  sap_created_at: Type.String(),
  prorate: Type.Boolean(),
  promo_code: Type.String(),
  is_from_sap: Type.Boolean(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  archived_at: Type.Integer({ minimum: 0 }),
});

export const FreeGoodPromo = Type.Object({
  order_type: Type.String(),
  sap_no_shipto: Nullable(Type.Number()),
  sales_org_shipto: Type.String(),
  distribution_channel: Nullable(Type.String()),
  cg_1_business_segmentation: Nullable(Type.String()),
  cg_2_business_category: Nullable(Type.String()),
  cg_3_business_type: Nullable(Type.String()),
  attribute_6: Nullable(Type.String()),
  attribute_8: Nullable(Type.String()),
  attribute_9: Nullable(Type.String()),
  product_brand_name: Nullable(Type.String()),
  product_type: Nullable(Type.String()),
  product_item_code: Nullable(Type.Integer()),
  condition_type: Nullable(Type.String()),
  sequential_number_for_free_goods_scale: Type.Integer(),
  uom_base_material: Nullable(Type.String()),
  uom_free_goods: Nullable(Type.String()),
  quantity_free_goods: Nullable(Type.Integer()),
  minimum_order_quantity: Type.Integer({ minimum: 0 }),
  minimum_order_amount: Type.Integer({ minimum: 0 }),
  sum_minimum_order_quantity: Type.Integer({ minimum: 0 }),
  sum_minimum_order_amount: Type.Integer({ minimum: 0 }),
  free_goods_product_item_code: Nullable(Type.Number()),
  free_goods_category: Type.Union([
    Type.Literal('inclusive'),
    Type.Literal('exclusive'),
  ]),
  valid_from: Type.Integer(),
  valid_until: Type.Integer(),
  sap_created_at: Type.String(),
  promo_code: Type.String(),
  is_from_sap: Type.Boolean(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const FreeGoodPromoInactive = Type.Object({
  order_type: Type.String(),
  sap_no_shipto: Nullable(Type.Number()),
  sales_org_shipto: Type.String(),
  distribution_channel: Nullable(Type.String()),
  cg_1_business_segmentation: Nullable(Type.String()),
  cg_2_business_category: Nullable(Type.String()),
  cg_3_business_type: Nullable(Type.String()),
  attribute_6: Nullable(Type.String()),
  attribute_8: Nullable(Type.String()),
  attribute_9: Nullable(Type.String()),
  product_brand_name: Nullable(Type.String()),
  product_type: Nullable(Type.String()),
  product_item_code: Nullable(Type.Integer()),
  condition_type: Nullable(Type.String()),
  sequential_number_for_free_goods_scale: Type.Integer(),
  uom_base_material: Nullable(Type.String()),
  uom_free_goods: Nullable(Type.String()),
  quantity_free_goods: Nullable(Type.Integer()),
  minimum_order_quantity: Type.Integer({ minimum: 0 }),
  minimum_order_amount: Type.Integer({ minimum: 0 }),
  sum_minimum_order_quantity: Type.Integer({ minimum: 0 }),
  sum_minimum_order_amount: Type.Integer({ minimum: 0 }),
  free_goods_product_item_code: Nullable(Type.Number()),
  free_goods_category: Type.Union([
    Type.Literal('inclusive'),
    Type.Literal('exclusive'),
  ]),
  valid_from: Type.Integer(),
  valid_until: Type.Integer(),
  sap_created_at: Type.String(),
  promo_code: Type.String(),
  is_from_sap: Type.Boolean(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  archived_at: Type.Integer({ minimum: 0 }),
});

export const GroupedPromo = Type.Object({
  id: Type.String({ format: 'uuid' }),
  promo_name: Type.String(),
  promo_code: Type.String(),
  promo_type: Type.Union([
    Type.Literal('free_goods'),
    Type.Literal('discounts'),
  ]),
  banner_url: Nullable(Type.String()),
  valid_from: Type.Integer(),
  valid_until: Type.Integer(),
  terms_and_conditions: Nullable(Type.String()),
  is_enabled: Nullable(Type.Boolean()),
  mechanism: Nullable(Type.String()),
  index: Type.Integer(),
  status: Type.Union([
    Type.Literal('inactive'),
    Type.Literal('not_yet_active'),
    Type.Literal('active'),
  ]),
  is_display_on_homepage: Type.Boolean(),
  is_mechanism_manually_updated: Type.Boolean(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  group_id: Type.Optional(Type.String()),
  product_highlight_number: Type.Optional(Type.Integer()),
});

// Order Promos
export const OrderPromo = Type.Object({
  id: Type.String({ format: 'uuid' }),
  order_id: Type.String({ format: 'uuid' }),
  single_free_good_promos: Type.Array(
    Type.Intersect([
      Type.Pick(GroupedPromo, ['id', 'mechanism']),
      Type.Pick(FreeGoodPromo, [
        'promo_code',
        'sequential_number_for_free_goods_scale',
        'free_goods_category',
        'quantity_free_goods',
        'minimum_order_quantity',
        'minimum_order_amount',
        'valid_from',
        'valid_until',
        'free_goods_product_item_code',
      ]),
      Type.Pick(Product, ['title', 'sales_unit', 'sales_unit_desc']),
      Type.Object({
        ref_product_id: Type.String({ format: 'uuid' }),
        product_id: Type.String({ format: 'uuid' }),
        product_item_code: Type.Integer(),
        product_images: Type.Array(Type.String()),
        product_image_thumbnails: Type.Array(Type.String()),
        product_images_medium: Type.Array(Type.String()),
        product_images_large: Type.Array(Type.String()),
        price: Type.Integer(),
      }),
    ])
  ),
  single_discount_promos: Type.Array(
    Type.Intersect([
      Type.Pick(GroupedPromo, ['id', 'mechanism']),
      Type.Pick(DiscountPromo, [
        'promo_code',
        'is_discount',
        'deduction_amount',
        'calculation_number',
        'minimum_order_quantity',
        'minimum_order_amount',
        'valid_from',
        'valid_until',
      ]),
      Type.Object({
        product_id: Type.String({ format: 'uuid' }),
        price_after_discount: Nullable(Type.Number()),
      }),
    ])
  ),
  bundled_free_good_promos: Type.Array(
    Type.Intersect([
      Type.Pick(GroupedPromo, ['id', 'mechanism']),
      Type.Pick(FreeGoodPromo, [
        'promo_code',
        'sequential_number_for_free_goods_scale',
        'free_goods_category',
        'quantity_free_goods',
        'sum_minimum_order_quantity',
        'sum_minimum_order_amount',
        'valid_from',
        'valid_until',
        'free_goods_product_item_code',
      ]),
      Type.Pick(Product, ['title', 'sales_unit', 'sales_unit_desc']),
      Type.Object({
        ref_product_id: Type.String({ format: 'uuid' }),
        product_id: Type.String({ format: 'uuid' }),
        product_item_code: Type.Integer(),
        product_images: Type.Array(Type.String()),
        product_image_thumbnails: Type.Array(Type.String()),
        product_images_medium: Type.Array(Type.String()),
        product_images_large: Type.Array(Type.String()),
        price: Type.Integer(),
      }),
    ])
  ),
  bundled_discount_promos: Type.Array(
    Type.Intersect([
      Type.Pick(GroupedPromo, ['id', 'mechanism']),
      Type.Pick(DiscountPromo, [
        'promo_code',
        'is_discount',
        'deduction_amount',
        'calculation_number',
        'sum_minimum_order_quantity',
        'sum_minimum_order_amount',
        'prorate',
        'valid_from',
        'valid_until',
      ]),
    ])
  ),
  compiled_product_discounts: Type.Array(
    Type.Object({
      product_id: Type.String({ format: 'uuid' }),
      is_discount: Type.Boolean(),
      deduction_amount: Type.Integer(),
      price_after_discount: Nullable(Type.Number()),
    })
  ),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

// ---------------------------------------------------------------------------
// Order Vouchers
// ---------------------------------------------------------------------------
export const OrderVoucher = Type.Object({
  id: Type.String({ format: 'uuid' }),
  order_meta_id: Type.String({ format: 'uuid' }),
  order_id: Type.String({ format: 'uuid' }),
  voucher_id: Type.String({ format: 'uuid' }),
  voucher_code: Type.String({
    minLength: 1,
    maxLength: VOUCHER_CODE_MAX_LENGTH,
  }),
  voucher_title: Type.String(),
  voucher_target_type: Type.Union([
    Type.Literal('GENERAL'),
    Type.Literal('SHIP_TO'),
  ]),
  reward_type: Type.Union([
    Type.Literal('DISCOUNT_PERCENT'),
    Type.Literal('DISCOUNT_AMOUNT'),
    Type.Literal('FREE_GOODS'),
  ]),
  total_discount_amount: Nullable(Type.Number({ minimum: 0 })),
  min_purchase_amount: Nullable(Type.Number({ minimum: 0 })),
  min_total_quantity_all_item: Nullable(Type.Integer({ minimum: 0 })),
  is_bundling: Type.Boolean({ default: false }),
  allow_other_vouchers: Type.Boolean({ default: false }),
  allow_other_promos: Type.Boolean({ default: false }),
  allow_special_prices: Type.Boolean({ default: false }),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const OrderVoucherRewardDiscount = Type.Object({
  id: Type.String({ format: 'uuid' }),
  order_voucher_id: Type.String({ format: 'uuid' }),
  product_item_code: Type.Integer(),
  product_name: Type.String(),
  division: Order.properties.division,
  quantity: Type.Integer({ minimum: 0 }),
  original_subtotal: Type.Number({ minimum: 0 }),
  discount_amount: Nullable(Type.Number({ minimum: 0 })),
  discount_percent: Nullable(Type.Number({ minimum: 0, maximum: 100 })),
  saved_amount: Type.Number({ minimum: 0 }),
  final_subtotal: Type.Number({ minimum: 0 }),
});

export const OrderVoucherRewardFreeGood = Type.Object({
  id: Type.String({ format: 'uuid' }),
  order_voucher_id: Type.String({ format: 'uuid' }),
  product_item_code: Type.Integer(),
  product_name: Type.String(),
  division: Order.properties.division,
  quantity: Type.Integer({ minimum: 0 }),
  saved_amount: Type.Number({ minimum: 0 }),
});

// User Passwords
export const UserPasswords = Type.Object({
  user_id: Type.String({ format: 'uuid' }),
  password: Nullable(Type.String()),
});

// ---------------------------------------------------------------------------
// Group Promo Tables
// ---------------------------------------------------------------------------

export const GroupedPromoProducts = Type.Object({
  product_item_code: Type.Integer(),
  group_id: Type.String(),
  index: Type.Integer(),
});

export const GroupedPromoCodes = Type.Object({
  group_id: Type.String(),
  promo_code: Type.String(),
  created_at: Type.String(),
});

export const GroupedPromosInactive = Type.Object({
  id: Type.String({ format: 'uuid' }),
  promo_name: Type.String(),
  promo_code: Type.String(),
  promo_type: Type.Union([
    Type.Literal('free_goods'),
    Type.Literal('discounts'),
  ]),
  banner_url: Nullable(Type.String()),
  valid_from: Type.Integer(),
  valid_until: Type.Integer(),
  terms_and_conditions: Nullable(Type.String()),
  is_enabled: Nullable(Type.Boolean()),
  mechanism: Nullable(Type.String()),
  index: Type.Integer(),
  status: Type.Union([
    Type.Literal('inactive'),
    Type.Literal('not_yet_active'),
    Type.Literal('active'),
  ]),
  is_display_on_homepage: Type.Boolean(),
  is_mechanism_manually_updated: Type.Boolean(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  group_id: Type.Optional(Type.String()),
  product_highlight_number: Type.Optional(Type.Integer()),
  archived_at: Type.Integer({ minimum: 0 }),
});

export const GroupedPromoCodesInactive = Type.Object({
  group_id: Type.String(),
  promo_code: Type.String(),
  created_at: Type.String(),
  archived_at: Type.Integer({ minimum: 0 }),
});

export const GroupedPromoProductsInactive = Type.Object({
  product_item_code: Type.Integer(),
  group_id: Type.String(),
  index: Type.Integer(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  archived_at: Type.Integer({ minimum: 0 }),
});

export const PromoHighlightIndexingCg01 = Type.Object({
  index: Type.Integer(),
  group_id: Type.String(),
  product_highlight_number: Type.Integer(),
});

export const PromoHighlightIndexingCg10 = PromoHighlightIndexingCg01;

// ---------------------------------------------------------------------------
// Webhook
// ---------------------------------------------------------------------------

export const WebhookPlu = Type.Object({
  id: Type.String({ format: 'uuid' }),
  status: Type.Union(
    [
      Type.Literal('requested'),
      Type.Literal('processed'),
      Type.Literal('failed'),
      Type.Literal('product_not_found'),
      Type.Literal('completed'),
    ],
    {
      description:
        'Status of the webhook PLU: requested, processed, failed, completed',
    }
  ),
  price_id: Type.String(),
  product_item_code: Type.Integer(),
  distribution_channel: Type.String(),
  cg_1_business_segmentation: Nullable(Type.String()),
  cg_2_business_category: Nullable(Type.String()),
  cg_3_business_type: Nullable(Type.String()),
  cg_4_manufacture: Nullable(Type.String()),
  cg_5_trading: Nullable(Type.String()),
  valuation_type: Nullable(Type.String()),
  price: Type.Integer(),
  valid_from: Nullable(Type.Integer()),
  valid_until: Nullable(Type.Integer()),
  sap_updated_at: Nullable(Type.Integer()),
  uom: Nullable(Type.String()),
  price_order: Nullable(Type.Integer()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const WebhookPlk = Type.Object({
  id: Type.String({ format: 'uuid' }),
  status: Type.Union(
    [
      Type.Literal('requested'),
      Type.Literal('processed'),
      Type.Literal('failed'),
      Type.Literal('product_not_found'),
      Type.Literal('shipto_not_found'),
      Type.Literal('completed'),
    ],
    {
      description:
        'Status of the webhook PLK: requested, processed, failed, completed',
    }
  ),
  price_id: Type.String(),
  product_item_code: Type.Integer(),
  distribution_channel: Nullable(Type.String()),
  valuation_type: Nullable(Type.String()),
  price: Type.Integer(),
  valid_from: Nullable(Type.Integer()),
  valid_until: Nullable(Type.Integer()),
  sap_no_shipto: Nullable(Type.Integer()),
  sap_updated_at: Nullable(Type.Integer()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const WebhookProductImage = Type.Object({
  id: Type.String({ format: 'uuid' }),
  event_id: Type.String(),
  action: Type.Union([Type.Literal('upsert'), Type.Literal('delete')]),
  item_code: Type.Integer(),
  index: Nullable(Type.Integer()),
  filename: Nullable(Type.String()),
  s3_key: Nullable(Type.String()),
  status: Type.Union(
    [
      Type.Literal('requested'),
      Type.Literal('processing'),
      Type.Literal('completed'),
      Type.Literal('failed'),
      Type.Literal('skipped'),
    ],
    {
      description:
        'Status of webhook product image: requested, processing, completed, failed, skipped',
    }
  ),
  last_error: Nullable(Type.String()),
  received_at: Type.Integer({ minimum: 0 }),
  processed_at: Nullable(Type.Integer({ minimum: 0 })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const MasterDataBusinessTypes = Type.Object({
  id: Type.String({ format: 'uuid' }),
  business_type_id: Type.String(),
  business_type_desc: Type.String(),
  business_category_id: Nullable(Type.String()),
  business_category_desc: Nullable(Type.String()),
  business_segmentation_id: Nullable(Type.String()),
  business_segmentation_desc: Nullable(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const MasterDataBusinessCategories = Type.Object({
  id: Type.String({ format: 'uuid' }),
  cg_2_business_category: Type.String(),
  cg_2_business_category_desc: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const CollectionPlant = Type.Object({
  id: Type.String({ format: 'uuid' }),
  name: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const MasterDataPlants = Type.Object({
  id: Type.String({ format: 'uuid' }),
  plant_id: Type.String(),
  plant_name: Nullable(Type.String()),
  plant_id_and_name: Nullable(Type.String()),
  plant_code: Nullable(Type.String()),
  plant_dc: Nullable(Type.String()),
  distribution_channel: Nullable(Type.String()),
  collection_plant_id: Nullable(Type.String({ format: 'uuid' })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const CollectionLhc = Type.Object({
  id: Type.String({ format: 'uuid' }),
  lhc_no: Type.String(),
  lhc_date: Type.String({ format: 'date' }),
  collection_plant_id: Type.String({ format: 'uuid' }),
  plant_name: Type.String(),
  total_invoice_count: Type.Integer(),
  total_invoice_amount: Type.Integer(),
  total_mutation_amount: Type.Integer(),
  total_difference_amount: Type.Integer(),
  created_at: Type.Integer({ minimum: 0 }),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  updated_at: Type.Integer({ minimum: 0 }),
  updated_by: Nullable(Type.String({ format: 'uuid' })),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
  deleted_by: Nullable(Type.String({ format: 'uuid' })),
});

export const CollectionLhcItem = Type.Object({
  id: Type.String({ format: 'uuid' }),
  lhc_id: Type.String({ format: 'uuid' }),
  delivery_order_id: Nullable(Type.String({ format: 'uuid' })),
  display_order: Type.Integer(),
  po_number: Nullable(Type.String()),
  so_number: Nullable(Type.String()),
  delivery_order_number: Type.String(),
  billing_date: Type.String({ format: 'date' }),
  billing_number: Type.String(),
  sap_no_bilto: Nullable(Type.Integer()),
  customer_name: Nullable(Type.String()),
  outlet_name: Nullable(Type.String()),
  sap_no_shipto: Nullable(Type.Integer()),
  po_amount: Type.Integer(),
  so_amount: Type.Integer(),
  billing_amount: Type.Integer(),
  difference_amount: Nullable(Type.Integer()),
  mutation_amount: Nullable(Type.Integer()),
  deposit_used_amount: Nullable(Type.Integer()),
  bank: Nullable(Type.String()),
  remark: Nullable(Type.String()),
  order_meta_id: Nullable(Type.String({ format: 'uuid' })),
  created_at: Type.Integer({ minimum: 0 }),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  updated_at: Type.Integer({ minimum: 0 }),
  updated_by: Nullable(Type.String({ format: 'uuid' })),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
  deleted_by: Nullable(Type.String({ format: 'uuid' })),
});

export const CollectionLhcItemPaymentProof = Type.Object({
  id: Type.String({ format: 'uuid' }),
  lhc_item_id: Type.String({ format: 'uuid' }),
  order_payment_proof_id: Type.String({ format: 'uuid' }),
  created_at: Type.Integer({ minimum: 0 }),
});

export const CollectionImportFile = Type.Object({
  id: Type.String({ format: 'uuid' }),
  source: Type.String(),
  file_name: Type.String(),
  file_date: Type.String({ format: 'date' }),
  status: Type.String(),
  total_rows: Nullable(Type.Integer()),
  processed_rows: Nullable(Type.Integer()),
  error_log: Nullable(Type.Record(Type.String(), Type.Unknown())),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const BankMutation = Type.Object({
  id: Type.String({ format: 'uuid' }),
  mutation_date: Type.String({ format: 'date' }),
  mutation_no: Type.String(),
  bank_code: Type.String(),
  bank_name: Type.String(),
  bank_account_number: Type.String(),
  bank_account_name: Type.String(),
  total_amount: Type.Integer(),
  used_amount: Type.Integer(),
  is_fully_used: Type.Boolean(),
  memo_line: Nullable(Type.String()),
  remark: Nullable(Type.String()),
  user_id: Nullable(Type.String({ format: 'uuid' })),
  source_file_id: Nullable(Type.String({ format: 'uuid' })),
  raw_row: Nullable(Type.Record(Type.String(), Type.Unknown())),
  deposit_reconcile_status: Nullable(Type.String()),
  deposit_reconcile_result_code: Nullable(Type.String()),
  deposit_reconcile_po_number: Nullable(Type.String()),
  deposit_reconcile_attempt_count: Type.Integer({ minimum: 0 }),
  deposit_reconcile_last_attempt_at: Nullable(Type.Integer({ minimum: 0 })),
  deposit_reconcile_next_retry_at: Nullable(Type.Integer({ minimum: 0 })),
  deposit_reconcile_completed_at: Nullable(Type.Integer({ minimum: 0 })),
  deposit_reconcile_error_code: Nullable(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const CollectionBankMutationAllocation = Type.Object({
  id: Type.String({ format: 'uuid' }),
  bank_mutation_id: Nullable(Type.String({ format: 'uuid' })),
  po_allocation_id: Nullable(Type.String({ format: 'uuid' })),
  lhc_id: Type.String({ format: 'uuid' }),
  lhc_item_id: Type.String({ format: 'uuid' }),
  delivery_order_id: Nullable(Type.String({ format: 'uuid' })),
  billing_id: Nullable(Type.String({ format: 'uuid' })),
  allocated_amount: Type.Integer(),
  created_at: Type.Integer({ minimum: 0 }),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  updated_at: Type.Integer({ minimum: 0 }),
  updated_by: Nullable(Type.String({ format: 'uuid' })),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
  deleted_by: Nullable(Type.String({ format: 'uuid' })),
});

export const CollectionPoBankMutationAllocation = Type.Object({
  id: Type.String({ format: 'uuid' }),
  order_meta_id: Type.String({ format: 'uuid' }),
  bank_mutation_id: Type.String({ format: 'uuid' }),
  allocated_amount: Type.Integer({ minimum: 1 }),
  source_lhc_id: Nullable(Type.String({ format: 'uuid' })),
  created_at: Type.Integer({ minimum: 0 }),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  updated_at: Type.Integer({ minimum: 0 }),
  updated_by: Nullable(Type.String({ format: 'uuid' })),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
  deleted_by: Nullable(Type.String({ format: 'uuid' })),
});

export const BankMutationSyncRequest = Type.Object({
  id: Type.String({ format: 'uuid' }),
  status: Type.String(),
  request_payload: Nullable(Type.Record(Type.String(), Type.Unknown())),
  response_payload: Nullable(Type.Record(Type.String(), Type.Unknown())),
  error_log: Nullable(Type.Record(Type.String(), Type.Unknown())),
  started_at: Nullable(Type.Integer({ minimum: 0 })),
  completed_at: Nullable(Type.Integer({ minimum: 0 })),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const DepositSyncRequest = Type.Object({
  id: Type.String({ format: 'uuid' }),
  status: Type.String(),
  request_payload: Nullable(Type.Record(Type.String(), Type.Unknown())),
  response_payload: Nullable(Type.Record(Type.String(), Type.Unknown())),
  error_log: Nullable(Type.Record(Type.String(), Type.Unknown())),
  started_at: Nullable(Type.Integer({ minimum: 0 })),
  completed_at: Nullable(Type.Integer({ minimum: 0 })),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const BiltoDeposit = Type.Object({
  id: Type.String({ format: 'uuid' }),
  sap_no_bilto: Type.String(),
  deposit_amount: Type.Integer(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const BiltoDepositHistory = Type.Object({
  id: Type.String({ format: 'uuid' }),
  sap_no_bilto: Type.String(),
  transaction_at: Type.String(),
  bank_mutation_id: Nullable(Type.String({ format: 'uuid' })),
  bank_mutation_number: Nullable(Type.String()),
  billing_number: Nullable(Type.String()),
  billing_plant_name: Nullable(Type.String()),
  type: Type.String(),
  bank_mutation_amount: Nullable(Type.Integer()),
  billing_amount: Nullable(Type.Integer()),
  order_meta_id: Nullable(Type.String({ format: 'uuid' })),
  order_id: Nullable(Type.String({ format: 'uuid' })),
  po_number: Nullable(Type.String()),
  sub_po_number: Nullable(Type.String()),
  so_number: Nullable(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const BiltoLedger = Type.Object({
  id: Type.String({ format: 'uuid' }),
  sap_no_bilto: Type.String(),
  transaction_at: Type.String(),
  bank_mutation_id: Nullable(Type.String({ format: 'uuid' })),
  bank_mutation_number: Nullable(Type.String()),
  billing_number: Nullable(Type.String()),
  billing_plant_name: Nullable(Type.String()),
  type: Type.String(),
  bank_mutation_amount: Nullable(Type.Integer()),
  billing_amount: Nullable(Type.Integer()),
  order_meta_id: Nullable(Type.String({ format: 'uuid' })),
  order_id: Nullable(Type.String({ format: 'uuid' })),
  po_number: Nullable(Type.String()),
  sub_po_number: Nullable(Type.String()),
  so_number: Nullable(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const ShiptoDeposit = Type.Object({
  id: Type.String({ format: 'uuid' }),
  sap_no_shipto: Type.Integer(),
  sap_no_bilto: Type.Integer(),
  deposit_amount: Type.Integer({ minimum: 0 }),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const ShiptoDepositHistory = Type.Object({
  id: Type.String({ format: 'uuid' }),
  sap_no_shipto: Type.Integer(),
  sap_no_bilto: Type.Integer(),
  transaction_at: Type.String(),
  bank_mutation_id: Nullable(Type.String({ format: 'uuid' })),
  bank_mutation_number: Nullable(Type.String()),
  billing_number: Nullable(Type.String()),
  billing_plant_name: Nullable(Type.String()),
  type: Type.String(),
  bank_mutation_amount: Nullable(Type.Integer()),
  billing_amount: Nullable(Type.Integer()),
  deposit_amount: Type.Integer(),
  order_meta_id: Type.String({ format: 'uuid' }),
  order_id: Nullable(Type.String({ format: 'uuid' })),
  po_number: Type.String(),
  sub_po_number: Nullable(Type.String()),
  so_number: Nullable(Type.String()),
  cancel_reason_id: Nullable(Type.String()),
  cancel_reason_snapshot: Nullable(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const Billing = Type.Object({
  id: Type.String({ format: 'uuid' }),
  billing_number: Type.String(),
  billing_date: Type.String({ format: 'date' }),
  delivery_order_id: Nullable(Type.String({ format: 'uuid' })),
  delivery_order_number: Nullable(Type.String()),
  sales_order_number: Nullable(Type.String()),
  po_number: Nullable(Type.String()),
  account_id: Nullable(Type.String()),
  customer_name: Nullable(Type.String()),
  billing_status: Nullable(Type.String()),
  billing_time: Nullable(Type.String()),
  delivery_date: Nullable(Type.String({ format: 'date' })),
  deletion_flag: Nullable(Type.String()),
  sap_no_shipto: Nullable(Type.Integer()),
  sap_no_bilto: Nullable(Type.Integer()),
  subtotal_amount: Nullable(Type.Integer()),
  tax_amount: Nullable(Type.Integer()),
  total_amount: Nullable(Type.Integer()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const BillingDetail = Type.Object({
  id: Type.String({ format: 'uuid' }),
  billing_id: Nullable(Type.String({ format: 'uuid' })),
  billing_item: Nullable(Type.String()),
  document_rec_no: Nullable(Type.String()),
  item_code: Nullable(Type.Integer()),
  quantity: Type.Integer(),
  uom: Type.String(),
  total_amount: Type.Integer(),
  initial_price: Nullable(Type.Integer()),
  source_generated_at: Nullable(Type.Integer({ minimum: 0 })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const CollectionAuditTrail = Type.Object({
  id: Type.String({ format: 'uuid' }),
  entity_type: Type.String(),
  entity_id: Type.String({ format: 'uuid' }),
  action: Type.String(),
  old_data: Nullable(Type.Record(Type.String(), Type.Unknown())),
  new_data: Nullable(Type.Record(Type.String(), Type.Unknown())),
  changed_fields: Nullable(Type.Array(Type.String())),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  user_agent: Nullable(Type.String()),
  ip_address: Nullable(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
});

export const ProductShiptoRestriction = Type.Object({
  id: Type.String({ format: 'uuid' }),
  product_item_code: Type.Integer(),
  sap_no_shipto: Type.Integer(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

// ---------------------------------------------------------------------------
// Vouchers
// ---------------------------------------------------------------------------

export const Voucher = Type.Object({
  id: Type.String({ format: 'uuid' }),
  code: Type.String({
    minLength: 1,
    maxLength: VOUCHER_CODE_MAX_LENGTH,
  }),
  title: Type.String(),
  description: Nullable(Type.String()),
  banner_url: Nullable(Type.String()),
  period_start: Type.String({ format: 'date' }),
  period_end: Type.String({ format: 'date' }),
  display_order: Type.Integer(),
  quota_total: Type.Integer({ minimum: 0 }),
  quota_used: Type.Integer({ minimum: 0 }),
  max_use_per_customer: Nullable(Type.Integer({ minimum: 1 })),
  target_type: Type.Union([Type.Literal('GENERAL'), Type.Literal('SHIP_TO')]),
  shipto_csv_url: Nullable(Type.String()),
  acquisition_type: Type.Union([
    Type.Literal('REGULAR'),
    Type.Literal('ACCOUNT_ACTIVATION'),
    Type.Literal('NEW_BUYER'),
  ]),
  valid_days_since_signup: Nullable(Type.Integer({ minimum: 1 })),
  condition_type: Type.Union([
    Type.Literal('REGULAR'),
    Type.Literal('MIN_PURCHASE'),
  ]),
  min_purchase_amount: Nullable(Type.Number({ minimum: 0 })),
  allow_other_vouchers: Type.Boolean(),
  allow_other_promos: Type.Boolean(),
  allow_special_price_list: Type.Boolean(),
  status: Type.Union([
    Type.Literal('ACTIVE'),
    Type.Literal('INACTIVE'),
    Type.Literal('PENDING'),
    Type.Literal('FAILED'),
  ]),
  eligible_shipto_count: Nullable(Type.Integer({ minimum: 0 })),
  snapshot_error_message: Nullable(Type.String()),
  is_display: Type.Boolean(),
  created_by: Type.String({ format: 'uuid' }),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const VoucherCg1BusinessSegmentation = Type.Object({
  voucher_id: Type.String({ format: 'uuid' }),
  cg_1_business_segmentation: Type.String(),
});

export const VoucherCg2BusinessCategory = Type.Object({
  voucher_id: Type.String({ format: 'uuid' }),
  cg_2_business_category: Type.String(),
});

export const VoucherDistributionChannel = Type.Object({
  voucher_id: Type.String({ format: 'uuid' }),
  distribution_channel: Type.String(),
});

export const VoucherShiptoTarget = Type.Object({
  voucher_id: Type.String({ format: 'uuid' }),
  sap_no_shipto: Type.Integer(),
});

export const ProductShiptoFirstPoEffective = Type.Object({
  product_id: Type.String({ format: 'uuid' }),
  sap_no_shipto: Type.Integer(),
  first_po_effective_date: Type.Integer({ minimum: 0 }),
});

export const VoucherRewardRule = Type.Object({
  id: Type.String({ format: 'uuid' }),
  voucher_id: Type.String({ format: 'uuid' }),
  min_total_quantity_all_item: Nullable(Type.Integer({ minimum: 1 })),
  reward_type: Type.Union([
    Type.Literal('DISCOUNT_PERCENT'),
    Type.Literal('DISCOUNT_AMOUNT'),
    Type.Literal('FREE_GOODS'),
  ]),
  is_bundling: Type.Boolean(),
  created_at: Type.Integer({ minimum: 0 }),
});

export const VoucherRewardRuleProduct = Type.Object({
  reward_rule_id: Type.String({ format: 'uuid' }),
  product_item_code: Type.Integer(),
  min_quantity: Nullable(Type.Integer({ minimum: 1 })),
});

export const VoucherRewardRuleFreeGoods = Type.Object({
  reward_rule_id: Type.String({ format: 'uuid' }),
  product_item_code: Type.Integer(),
  quantity: Type.Integer({ minimum: 1 }),
});

export const VoucherRewardRuleDiscountProducts = Type.Object({
  reward_rule_id: Type.String({ format: 'uuid' }),
  product_item_code: Type.Integer(),
  discount_percent: Nullable(
    Type.Number({ exclusiveMinimum: 0, maximum: 100 })
  ),
  discount_amount: Nullable(Type.Number({ minimum: 0 })),
});

export const VoucherUsage = Type.Object({
  id: Type.String({ format: 'uuid' }),
  voucher_id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  sap_no_shipto: Nullable(Type.Integer()),
  order_id: Nullable(Type.String({ format: 'uuid' })),
  used_at: Type.Integer({ minimum: 0 }),
  quantity: Type.Integer({ minimum: 1 }),
});

export const OrderVoucherValidationError = Type.Object({
  id: Type.String({ format: 'uuid' }),
  order_id: Type.String({ format: 'uuid' }),
  order_meta_id: Type.String({ format: 'uuid' }),
  voucher_id: Nullable(Type.String({ format: 'uuid' })),
  voucher_code: Type.String(),
  source: Type.Union([
    Type.Literal('admin_submit'),
    Type.Literal('admin_validate_vouchers'),
    Type.Literal('so_automation'),
  ]),
  error_code: Type.String(),
  validation_date: Type.String({ format: 'date' }),
  po_effective_date: Nullable(Type.Integer({ minimum: 0 })),
  voucher_period_start: Nullable(Type.String({ format: 'date' })),
  voucher_period_end: Nullable(Type.String({ format: 'date' })),
  created_at: Type.Integer({ minimum: 0 }),
});

export const ShiptoVoucherUsage = Type.Object({
  id: Type.String({ format: 'uuid' }),
  sap_no_shipto: Type.Integer(),
  voucher_id: Type.String({ format: 'uuid' }),
  total_used: Type.Integer({ minimum: 0 }),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const RfcVoucherCreate = Type.Object({
  id: Type.String({ format: 'uuid' }),
  original_voucher_code: Type.String(),
  sap_voucher_code: Type.String(),
  user_id: Nullable(Type.String({ format: 'uuid' })),
  ship_to_id: Nullable(Type.String({ format: 'uuid' })),
  batch_request_id: Nullable(Type.String({ format: 'uuid' })),
  voucher_id: Type.String({ format: 'uuid' }),
  vkorg: Type.String(),
  soldto: Type.String(),
  matnr: Type.String(),
  amount: Type.Number(),
  konwa: Type.String(),
  valfr: Type.String({ format: 'date' }),
  valto: Type.String({ format: 'date' }),
  request_status: Type.Union([
    Type.Literal('request'), // The voucher creation record has been inserted into the database and is pending initiation of the synchronous RFC call to SAP.
    Type.Literal('success'), // The synchronous RFC call executed successfully, and SAP acknowledged the voucher creation.
    Type.Literal('failed'), // The RFC call failed (due to connection issue, timeout, or SAP validation error).
  ]),
  sap_synced_at: Nullable(Type.Integer({ minimum: 0 })),
  sap_response_message: Nullable(Type.String()),
  status: Type.Union([
    Type.Literal('available'), // The voucher has been successfully created in SAP and is ready to be used by the buyer or system for Sales Order (SO) creation.
    Type.Literal('assigned'), // The voucher is reserved or linked to an order creation process that requires further administrative confirmation or manual intervention before becoming a final Sales Order.
    Type.Literal('used'), // The voucher has been successfully applied to an order, and that order has been finalized as a Sales Order (SO) in the system.
    Type.Literal('pending_manual'), // The voucher has been reserved for an order that cannot automatically submit to SAP yet (e.g. bank transfer/manual processing).
    Type.Literal('unused'), // The voucher request was successful, but the voucher was never utilized by the system or customer within its validity period.
  ]),
  order_meta_id: Nullable(Type.String({ format: 'uuid' })),
  order_id: Nullable(Type.String({ format: 'uuid' })),
  created_at: Type.Integer({ minimum: 0 }),
  created_by: Nullable(Type.String({ format: 'uuid' })),
});

export const IntegrationSystem = Type.Object({
  id: Type.String({ format: 'uuid' }),
  name: Type.String(),
  username: Type.String(),
  password: Type.String(),
  is_active: Type.Boolean(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const IntegrationSystemPermission = Type.Object({
  id: Type.String({ format: 'uuid' }),
  integration_system_id: Type.String({ format: 'uuid' }),
  permission_key: Type.String(),
});

export const IntegrationOrderDownload = Type.Object({
  id: Type.String({ format: 'uuid' }),
  order_meta_id: Type.String({ format: 'uuid' }),
  order_id: Type.String({ format: 'uuid' }),
  integration_system_id: Type.String({ format: 'uuid' }),
  downloaded_at: Type.Integer({ minimum: 0 }),
  ip_address: Nullable(Type.String()),
  user_agent: Nullable(Type.String()),
});

export const SalesOrderAutomationHolidayConfiguration = Type.Object({
  id: Type.String({ format: 'uuid' }),
  holiday_date: Type.String({ format: 'date' }),
  automation_eligible_start_time: Type.String(),
  automation_eligible_end_time: Type.String(),
  description: Nullable(Type.String()),
  is_active: Type.Boolean(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const FeatureFlag = Type.Object({
  id: Type.Integer(),
  key: Type.String({ maxLength: 255 }),
  value: Type.Unknown(),
  description: Nullable(Type.String()),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const Region = Type.Object({
  id: Type.String({ format: 'uuid' }),
  code: Type.String(),
  name: Type.String(),
  display_name: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const City = Type.Object({
  id: Type.String({ format: 'uuid' }),
  region_id: Type.String({ format: 'uuid' }),
  name: Type.String(),
  display_name: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const District = Type.Object({
  id: Type.String({ format: 'uuid' }),
  city_id: Type.String({ format: 'uuid' }),
  name: Type.String(),
  display_name: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const SubDistrict = Type.Object({
  id: Type.String({ format: 'uuid' }),
  district_id: Type.String({ format: 'uuid' }),
  name: Type.String(),
  display_name: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const PostalCode = Type.Object({
  id: Type.String({ format: 'uuid' }),
  sub_district_id: Type.String({ format: 'uuid' }),
  postal_code: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const RegistrationCityEmailMapping = Type.Object({
  id: Type.String({ format: 'uuid' }),
  province: Type.String(),
  city: Type.String(),
  normalized_city: Type.String(),
  closest_branch: Type.String(),
  email_head: Nullable(Type.String()),
  email_1: Nullable(Type.String()),
  email_2: Nullable(Type.String()),
  is_active: Type.Boolean(),
  priority: Type.Integer({ minimum: 0 }),
  source: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const RegistrationServiceArea = Type.Object({
  id: Type.String({ format: 'uuid' }),
  slug: Type.String({ maxLength: 50 }),
  name: Type.String({ maxLength: 200 }),
  display_order: Type.Integer({ minimum: 0 }),
  is_active: Type.Boolean(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const RegistrationBusinessSegmentation = Type.Object({
  id: Type.String({ format: 'uuid' }),
  code: Type.String({ maxLength: 50 }),
  name: Type.String({ maxLength: 200 }),
  desc: Nullable(Type.String()),
  icon: Nullable(Type.String()),
  display_order: Type.Integer({ minimum: 0 }),
  is_active: Type.Boolean(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const RegistrationTnc = Type.Object({
  id: Type.String({ format: 'uuid' }),
  version: Type.Integer(),
  title: Type.String({ maxLength: 200 }),
  body: Type.String(),
  data_processing_label: Type.String(),
  marketing_label: Type.String(),
  is_active: Type.Boolean(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const RegistrationConsent = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  tnc_id: Type.String({ format: 'uuid' }),
  type: Type.Union([
    Type.Literal('data_processing'),
    Type.Literal('marketing'),
  ]),
  accepted: Type.Boolean(),
  label: Type.String(),
  created_at: Type.Integer({ minimum: 0 }),
});

export const RegistrationMdgLog = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  mdg_reference: Type.String({ maxLength: 64 }),
  request: Type.Record(Type.String(), Type.Unknown()),
  response: Type.Record(Type.String(), Type.Unknown()),
  created_at: Type.Integer({ minimum: 0 }),
});

const RegistrationMdgReviewFieldItem = Type.Object({
  field_name: Type.String(),
  note: Type.String(),
});

export const RegistrationMdgStatusWebhookLog = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  ticket_number_sol: Type.String({ maxLength: 64 }),
  request: Type.Unknown(),
  created_at: Type.Integer({ minimum: 0 }),
});

export const RegistrationMdgReview = Type.Object({
  ticket_number: Type.String({ maxLength: 64 }),
  user_id: Type.String({ format: 'uuid' }),
  party_type: Type.Union([Type.Literal('bill_to'), Type.Literal('ship_to')]),
  outlet_id: Type.Optional(Nullable(Type.String({ format: 'uuid' }))),
  status: Type.Union([
    Type.Literal('IN_PROGRESS'),
    Type.Literal('APPROVED'),
    Type.Literal('REJECTED'),
  ]),
  current_approval_level: Type.Integer({ minimum: 1 }),
  current_approval_level_name: Type.String(),
  current_approval_pic_name: Type.String(),
  note: Type.Optional(Nullable(Type.String())),
  fields: Type.Array(RegistrationMdgReviewFieldItem),
  sap_number: Type.Optional(Nullable(Type.String())),
  occurred_at: Type.Optional(Nullable(Type.Integer({ minimum: 0 }))),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const RegistrationMdgReviewHistory = Type.Object({
  id: Type.String({ format: 'uuid' }),
  webhook_log_id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  party_type: Type.Union([Type.Literal('bill_to'), Type.Literal('ship_to')]),
  ticket_number: Type.String({ maxLength: 64 }),
  outlet_id: Type.Optional(Nullable(Type.String({ format: 'uuid' }))),
  status: Type.Union([
    Type.Literal('IN_PROGRESS'),
    Type.Literal('APPROVED'),
    Type.Literal('REJECTED'),
  ]),
  current_approval_level: Type.Integer({ minimum: 1 }),
  current_approval_level_name: Type.String(),
  current_approval_pic_name: Type.String(),
  note: Type.Optional(Nullable(Type.String())),
  fields: Type.Array(RegistrationMdgReviewFieldItem),
  sap_number: Type.Optional(Nullable(Type.String())),
  occurred_at: Type.Optional(Nullable(Type.Integer({ minimum: 0 }))),
  created_at: Type.Integer({ minimum: 0 }),
});

export const RegistrationMdgFieldMapping = Type.Object({
  id: Type.String({ format: 'uuid' }),
  party: Type.Union([
    Type.Literal('bill_to_company'),
    Type.Literal('bill_to_individual'),
    Type.Literal('ship_to_company'),
    Type.Literal('ship_to_individual'),
  ]),
  sort_order: Type.Integer(),
  field_name: Nullable(Type.String()),
  content_label_desc: Nullable(Type.String()),
  content_datatype: Nullable(
    Type.Union([
      Type.Literal('TEXT'),
      Type.Literal('NUMERIC'),
      Type.Literal('ATTACHMENT'),
      Type.Literal('TIME'),
    ])
  ),
  operator: Type.Union([
    Type.Literal('copy'),
    Type.Literal('constant'),
    Type.Literal('concat'),
    Type.Literal('slice'),
    Type.Literal('map'),
    Type.Literal('join'),
    Type.Literal('repeat'),
  ]),
  params: Type.Unknown(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const RegistrationBusinessType = Type.Object({
  id: Type.String({ format: 'uuid' }),
  code: Type.String({ maxLength: 50 }),
  name: Type.String({ maxLength: 200 }),
  icon: Nullable(Type.String()),
  display_order: Type.Integer({ minimum: 0 }),
  is_active: Type.Boolean(),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const RegistrationBusinessProfile = Type.Object({
  user_id: Type.String({ format: 'uuid' }),
  ownership_type: Type.String({ maxLength: 20 }),
  status: Type.String({ maxLength: 30 }),
  status_updated_at: Nullable(Type.Integer({ minimum: 0 })),
  npwp_number: Nullable(Type.String({ maxLength: 20 })),
  company_type: Nullable(Type.String({ maxLength: 30 })),
  company_name: Nullable(Type.String({ maxLength: 255 })),
  company_address: Nullable(Type.String()),
  region_id: Nullable(Type.String({ format: 'uuid' })),
  city_id: Nullable(Type.String({ format: 'uuid' })),
  district_id: Nullable(Type.String({ format: 'uuid' })),
  sub_district_id: Nullable(Type.String({ format: 'uuid' })),
  postal_code_id: Nullable(Type.String({ format: 'uuid' })),
  region_label: Nullable(Type.String({ maxLength: 255 })),
  city_label: Nullable(Type.String({ maxLength: 255 })),
  district_label: Nullable(Type.String({ maxLength: 255 })),
  sub_district_label: Nullable(Type.String({ maxLength: 255 })),
  postal_code_label: Nullable(Type.String({ maxLength: 20 })),
  pic_nik: Nullable(Type.String({ maxLength: 16 })),
  pic_first_name: Nullable(Type.String({ maxLength: 100 })),
  pic_last_name: Nullable(Type.String({ maxLength: 100 })),
  pic_address: Nullable(Type.String()),
  pic_rt: Nullable(Type.String({ maxLength: 5 })),
  pic_rw: Nullable(Type.String({ maxLength: 5 })),
  pic_phone: Nullable(Type.String({ maxLength: 20 })),
  pic_email: Nullable(Type.String({ maxLength: 255 })),
  owner_nik: Nullable(Type.String({ maxLength: 16 })),
  owner_first_name: Nullable(Type.String({ maxLength: 100 })),
  owner_last_name: Nullable(Type.String({ maxLength: 100 })),
  owner_address: Nullable(Type.String()),
  owner_rt: Nullable(Type.String({ maxLength: 5 })),
  owner_rw: Nullable(Type.String({ maxLength: 5 })),
  owner_phone: Nullable(Type.String({ maxLength: 20 })),
  owner_email: Nullable(Type.String({ maxLength: 255 })),
  company_npwp_url: Nullable(Type.String()),
  company_npwp_original_filename: Nullable(Type.String()),
  pic_ktp_url: Nullable(Type.String()),
  pic_ktp_original_filename: Nullable(Type.String()),
  pic_selfie_ktp_url: Nullable(Type.String()),
  pic_selfie_ktp_original_filename: Nullable(Type.String()),
  owner_ktp_url: Nullable(Type.String()),
  owner_ktp_original_filename: Nullable(Type.String()),
  owner_selfie_ktp_url: Nullable(Type.String()),
  owner_selfie_ktp_original_filename: Nullable(Type.String()),
  code_referral: Nullable(Type.String({ maxLength: 100 })),
  mdg_ticket_number: Type.Optional(Nullable(Type.String({ maxLength: 64 }))),
  mdg_row_index: Type.Optional(Nullable(Type.Integer({ minimum: 0 }))),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const RegistrationOutlet = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  is_primary: Type.Boolean(),
  status: Type.String({ maxLength: 30 }),
  status_updated_at: Nullable(Type.Integer({ minimum: 0 })),
  name: Nullable(Type.String({ maxLength: 255 })),
  social_media: Nullable(Type.String()),
  online_order_merchant_link: Nullable(Type.String()),
  phone: Nullable(Type.String({ maxLength: 20 })),
  email: Nullable(Type.String({ maxLength: 255 })),
  address: Nullable(Type.String()),
  latitude: Nullable(Type.Number()),
  longitude: Nullable(Type.Number()),
  display_order: Type.Integer(),
  cg_3_business_type: Nullable(Type.String()),
  use_primary_outlet_menu: Type.Boolean(),
  google_map_place_id: Nullable(Type.String()),
  google_map_types: Nullable(Type.Array(Type.String())),
  google_map_rating: Nullable(Type.Number()),
  google_map_user_ratings_total: Nullable(Type.Integer()),
  region_id: Nullable(Type.String({ format: 'uuid' })),
  city_id: Nullable(Type.String({ format: 'uuid' })),
  district_id: Nullable(Type.String({ format: 'uuid' })),
  sub_district_id: Nullable(Type.String({ format: 'uuid' })),
  postal_code_id: Nullable(Type.String({ format: 'uuid' })),
  region_label: Nullable(Type.String({ maxLength: 255 })),
  city_label: Nullable(Type.String({ maxLength: 255 })),
  district_label: Nullable(Type.String({ maxLength: 255 })),
  sub_district_label: Nullable(Type.String({ maxLength: 255 })),
  postal_code_label: Nullable(Type.String({ maxLength: 20 })),
  pic_role: Nullable(Type.String({ maxLength: 30 })),
  pic_role_other: Nullable(Type.String({ maxLength: 255 })),
  mdg_ticket_number: Type.Optional(Nullable(Type.String({ maxLength: 64 }))),
  mdg_row_index: Type.Optional(Nullable(Type.Integer({ minimum: 0 }))),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const RegistrationOutletAttachment = Type.Object({
  id: Type.String({ format: 'uuid' }),
  outlet_id: Type.String({ format: 'uuid' }),
  attachment_type: Type.String({ maxLength: 30 }),
  file_url: Type.String(),
  original_filename: Nullable(Type.String({ maxLength: 255 })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_at: Type.Integer({ minimum: 0 }),
});

export const RegistrationProductReference = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  product_id: Type.String({ format: 'uuid' }),
  item_code: Type.Integer(),
  created_at: Type.Integer({ minimum: 0 }),
});

export const RegistrationUserStatusHistory = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  status_before: Nullable(Type.String({ maxLength: 30 })),
  status_after: Type.String({ maxLength: 30 }),
  note: Nullable(Type.String()),
  origin: Nullable(Type.String({ maxLength: 20 })),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  created_at: Type.Integer({ minimum: 0 }),
});

export const RegistrationBusinessProfileStatusHistory = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  status_before: Nullable(Type.String({ maxLength: 30 })),
  status_after: Type.String({ maxLength: 30 }),
  note: Nullable(Type.String()),
  origin: Nullable(Type.String({ maxLength: 20 })),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  created_at: Type.Integer({ minimum: 0 }),
});

export const RegistrationOutletStatusHistory = Type.Object({
  id: Type.String({ format: 'uuid' }),
  outlet_id: Type.String({ format: 'uuid' }),
  status_before: Nullable(Type.String({ maxLength: 30 })),
  status_after: Type.String({ maxLength: 30 }),
  note: Nullable(Type.String()),
  origin: Nullable(Type.String({ maxLength: 20 })),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  created_at: Type.Integer({ minimum: 0 }),
});

export const ShiptoFeatureSegment = Type.Object({
  id: Type.String({ format: 'uuid' }),
  sap_no_shipto: Type.Integer(),
  feature_segment: Type.String({ maxLength: 100 }),
  created_by: Nullable(Type.String({ format: 'uuid' })),
  created_at: Type.Integer({ minimum: 0 }),
  updated_by: Nullable(Type.String({ format: 'uuid' })),
  updated_at: Type.Integer({ minimum: 0 }),
  deleted_by: Nullable(Type.String({ format: 'uuid' })),
  deleted_at: Nullable(Type.Integer({ minimum: 0 })),
});

export const DbSchema = {
  // ----------------------------------
  // User & Auth
  // ----------------------------------
  users: User,
  reset_password_tokens: ResetPasswordToken,
  set_password_tokens: SetPasswordToken,
  user_bill_tos: UserBillTo,
  bil_tos: BilTo,
  ship_tos: ShipTo,
  user_ship_tos: UserShipTo,
  ship_to_contacts: ShipToContact,
  user_registration_tickets: UserRegistrationTickets,
  sessions: Session,
  // ----------------------------------
  // Brands
  // ----------------------------------
  brands: Brand,
  certifications: Certification,
  brand_certifications: BrandCertification,
  categories: Category,
  brand_categories: BrandCategory,
  brand_highlights: BrandHighlights,
  // ----------------------------------
  // Product
  // ----------------------------------
  product_categories: ProductCategory,
  product_sub_categories: ProductSubCategory,
  product_sub_third_categories: ProductSubThirdCategory,
  products: Product,
  product_keywords: ProductKeyword,
  product_images: ProductImage,
  product_highlights: ProductHighlight,
  product_highlight_items: ProductHighlightItem,
  product_highlight_shiptos: ProductHighlightShipto,
  product_highlight_business_types: ProductHighlightBusinessType,
  product_prices: ProductPrice,
  product_stocks: ProductStock,
  product_stock_days: ProductStockDay,
  product_valuation_type: ProductValuationType,
  product_exclude_branches: ProductExcludeBranch,
  product_exclude_customers: ProductExcludeCustomer,
  product_exclude_publics: ProductExcludePublic,
  product_priority_publics: ProductPriorityPublic,
  product_daily_order_limit_configurations: ProductDailyOrderLimitConfiguration,
  product_payment_method_configurations: ProductPaymentMethodConfiguration,
  product_manual_process_configurations: ProductManualProcessConfiguration,
  product_manual_exclude_shipto_configurations:
    ProductManualExcludeShiptoConfiguration,
  product_manual_shipto_configurations: ProductManualShiptoConfiguration,
  rfc_product_stock: RfcProductStock,
  product_pareto: ProductPareto,
  // ----------------------------------
  // Product Pricing
  // ----------------------------------
  product_common_prices: ProductCommonPrice,
  product_special_prices: ProductSpecialPrice,
  shipto_order_valuation_types: ShiptoOrderValuationType,

  // ----------------------------------
  // Cart
  // ----------------------------------
  carts: Cart,
  cart_recommendation_scoring_config_sets: CartRecommendationScoringConfigSet,
  cart_recommendation_scoring_config_values:
    CartRecommendationScoringConfigValue,
  cart_recommendation_generation_runs: CartRecommendationGenerationRun,
  cart_recommendation_step_logs: CartRecommendationStepLog,
  cart_recommendation_product_carts: CartRecommendationProductCart,
  cart_recommendation_product_candidates: CartRecommendationProductCandidate,
  cart_recommendation_products: CartRecommendationProduct,

  // ----------------------------------
  // Order Minimum Spend
  // ----------------------------------
  minimum_spends: MinimumSpend,

  // ----------------------------------
  // Email Notification
  // ----------------------------------
  email_notifications: EmailNotification,
  email_attempts: EmailAttempt,

  // ----------------------------------
  // Ship-to Notification
  // ----------------------------------
  shipto_restock_profiles: ShiptoRestockProfile,
  shipto_welcome_candidates: ShiptoWelcomeCandidate,
  shipto_notification_reminders: ShiptoNotificationReminder,
  shipto_notification_reminder_products: ShiptoNotificationReminderProduct,
  broadcast_push_notifications: BroadcastPushNotification,
  broadcast_push_notification_deliveries: BroadcastPushNotificationDelivery,

  // ----------------------------------
  // Order
  // ----------------------------------
  order_metas: OrderMeta,
  order_meta_generate_reports: OrderMetaGenerateReport,
  sales_orders: SalesOrder,
  sales_order_items: SalesOrderItem,
  orders: Order,
  order_items: OrderItem,
  delivery_orders: DeliveryOrder,
  delivery_order_items: DeliveryOrderItem,
  delivery_order_histories: DeliveryOrderHistory,
  delivery_order_automation_configurations:
    DeliveryOrderAutomationConfiguration,
  delivery_order_automation_eligibilities: DeliveryOrderAutomationEligibility,
  delivery_order_automation_exclude_customers:
    DeliveryOrderAutomationExcludeCustomer,
  sales_order_automation_holiday_configurations:
    SalesOrderAutomationHolidayConfiguration,
  order_webhooks: OrderWebhook,
  order_webhook_logs: OrderWebhookLog,
  order_webhook_detail_logs: OrderWebhookDetailLog,
  sales_order_webhook_logs: SalesOrderWebhookLog,
  sales_order_webhook_detail_logs: SalesOrderWebhookDetailLog,
  delivery_order_webhook_logs: DeliveryOrderWebhookLog,
  delivery_order_webhook_detail_logs: DeliveryOrderWebhookDetailLog,
  billing_webhook_logs: BillingWebhookLog,
  billing_webhook_detail_logs: BillingWebhookDetailLog,
  order_promos: OrderPromo,
  order_vouchers: OrderVoucher,
  order_voucher_reward_discounts: OrderVoucherRewardDiscount,
  order_voucher_reward_free_goods: OrderVoucherRewardFreeGood,
  // ----------------------------------
  // Order Payment
  // ----------------------------------
  order_payments: OrderPayment,
  order_payment_proofs: OrderPaymentProof,
  order_payment_proof_ocr_results: OrderPaymentProofOcrResult,
  order_po_docs: OrderPoDocs,
  // ----------------------------------
  // Order Template
  // ----------------------------------
  order_templates: OrderTemplate,
  order_template_items: OrderTemplateItem,

  // ----------------------------------
  // Order Status History
  // ----------------------------------
  order_status_histories: OrderStatusHistory,

  // ----------------------------------
  // Order Changelog
  // ----------------------------------
  order_changelogs: OrderChangelog,

  // ----------------------------------
  // Wishlist
  // ----------------------------------
  wishlist: Wishlist,

  // ----------------------------------
  // Banner
  // ----------------------------------
  banner: Banner,

  // ----------------------------------
  // Banner Shipto
  // ----------------------------------
  banner_shiptos: BannerShipto,

  // ----------------------------------
  // Banner CG3
  // ----------------------------------
  banner_business_types: BannerBusinessType,

  // ----------------------------------
  // Audit Log
  // ----------------------------------
  audit_logs: AuditLog,
  webportal_logs: WebPortalLog,
  // ----------------------------------
  // Admin Access
  // ----------------------------------
  admin_access: AdminAccess,

  // ----------------------------------
  // Bank Accounts
  // ----------------------------------
  bank_accounts: BankAccount,

  // ----------------------------------
  // Misc
  // ----------------------------------
  webportal_token: WebportalToken,
  // ----------------------------------
  // Cancel Reason
  // ----------------------------------
  cancel_reasons: CancelReason,
  // ----------------------------------
  // Tokens
  // ----------------------------------
  tokens: Tokens,
  order_status_mapping_config: OrderStatusMappingConfig,

  // ----------------------------------
  // Notifications
  // ----------------------------------
  notifications: Notification,

  // ----------------------------------
  // User Last Login
  // ----------------------------------
  user_last_logins: UserLastLogin,

  // ----------------------------------
  // Promos
  // ----------------------------------
  discount_promos: DiscountPromo,
  free_good_promos: FreeGoodPromo,
  grouped_promos: GroupedPromo,
  user_passwords: UserPasswords,
  discount_promos_inactive: DiscountPromoInactive,
  free_good_promos_inactive: FreeGoodPromoInactive,

  // ----------------------------------
  // Trend Insights
  // ----------------------------------
  trend_items: TrendItem,
  trend_item_metrics_30_days: TrendItemMetric30Days,
  trend_item_recipes: TrendItemRecipe,
  trend_item_products: TrendItemProduct,
  trend_item_brands: TrendItemBrand,
  trend_item_tiktok_videos: TrendItemTiktokVideo,
  tags: Tag,
  trend_item_tags: TrendItemTag,

  // ----------------------------------
  // Recipes
  // ----------------------------------
  recipes: Recipe,
  recipe_tags: RecipeTag,
  recipe_taggings: RecipeTagging,
  recipe_ingredient_groups: RecipeIngredientGroup,
  recipe_ingredients: RecipeIngredient,
  recipe_products: RecipeProduct,
  recipe_medias: RecipeMedia,
  recipe_favorites: RecipeFavorite,
  recipe_notifications: RecipeNotifications,
  recipe_sections: RecipeSection,
  recipe_section_items: RecipeSectionItem,
  recipe_business_types: RecipeBusinessType,
  // ----------------------------------
  // Grouped Promo
  // ----------------------------------
  grouped_promo_products: GroupedPromoProducts,
  grouped_promo_codes: GroupedPromoCodes,
  grouped_promos_inactive: GroupedPromosInactive,
  grouped_promo_codes_inactive: GroupedPromoCodesInactive,
  grouped_promo_products_inactive: GroupedPromoProductsInactive,
  promo_highlight_indexing_cg_01: PromoHighlightIndexingCg01,
  promo_highlight_indexing_cg_10: PromoHighlightIndexingCg10,
  webhook_plu: WebhookPlu,
  webhook_plk: WebhookPlk,
  webhook_product_images: WebhookProductImage,
  product_shipto_restrictions: ProductShiptoRestriction,
  // ----------------------------------
  // Vouchers
  // ----------------------------------
  vouchers: Voucher,
  voucher_cg_1_business_segmentations: VoucherCg1BusinessSegmentation,
  voucher_cg_2_business_categories: VoucherCg2BusinessCategory,
  voucher_distribution_channels: VoucherDistributionChannel,
  voucher_shipto_targets: VoucherShiptoTarget,
  product_shipto_first_po_effective: ProductShiptoFirstPoEffective,
  voucher_reward_rules: VoucherRewardRule,
  voucher_reward_rule_products: VoucherRewardRuleProduct,
  voucher_reward_rule_free_goods: VoucherRewardRuleFreeGoods,
  voucher_reward_rule_discount_products: VoucherRewardRuleDiscountProducts,
  voucher_usage: VoucherUsage,
  order_voucher_validation_errors: OrderVoucherValidationError,
  shipto_voucher_usages: ShiptoVoucherUsage,
  rfc_voucher_create: RfcVoucherCreate,
  // ----------------------------------
  // Master Data
  // ----------------------------------
  master_data_business_types: MasterDataBusinessTypes,
  master_data_business_categories: MasterDataBusinessCategories,
  collection_plants: CollectionPlant,
  master_data_plants: MasterDataPlants,
  collection_lhcs: CollectionLhc,
  collection_lhc_items: CollectionLhcItem,
  collection_lhc_item_payment_proofs: CollectionLhcItemPaymentProof,
  collection_import_files: CollectionImportFile,
  bank_mutations: BankMutation,
  collection_bank_mutation_allocations: CollectionBankMutationAllocation,
  collection_po_bank_mutation_allocations: CollectionPoBankMutationAllocation,
  bank_mutation_sync_requests: BankMutationSyncRequest,
  deposit_sync_requests: DepositSyncRequest,
  bilto_deposits: BiltoDeposit,
  bilto_deposit_histories: BiltoDepositHistory,
  bilto_ledgers: BiltoLedger,
  shipto_deposits: ShiptoDeposit,
  shipto_deposit_histories: ShiptoDepositHistory,
  billings: Billing,
  billing_details: BillingDetail,
  collection_audit_trails: CollectionAuditTrail,

  // ----------------------------------
  // Integration Systems
  // ----------------------------------
  integration_systems: IntegrationSystem,
  integration_system_permissions: IntegrationSystemPermission,
  integration_order_downloads: IntegrationOrderDownload,

  // ----------------------------------
  // Feature Flags
  // ----------------------------------
  feature_flags: FeatureFlag,

  // ----------------------------------
  // NOO (New Order Opportunity)
  // ----------------------------------
  registration_city_email_mappings: RegistrationCityEmailMapping,

  // ----------------------------------
  // Universal Registration
  // ----------------------------------
  registration_service_areas: RegistrationServiceArea,
  registration_business_segmentations: RegistrationBusinessSegmentation,
  registration_business_types: RegistrationBusinessType,
  registration_tncs: RegistrationTnc,
  registration_consents: RegistrationConsent,
  registration_mdg_logs: RegistrationMdgLog,
  registration_mdg_status_webhook_logs: RegistrationMdgStatusWebhookLog,
  registration_mdg_reviews: RegistrationMdgReview,
  registration_mdg_review_histories: RegistrationMdgReviewHistory,
  registration_mdg_field_mappings: RegistrationMdgFieldMapping,
  registration_business_profiles: RegistrationBusinessProfile,
  registration_outlets: RegistrationOutlet,
  registration_outlet_attachments: RegistrationOutletAttachment,
  registration_product_references: RegistrationProductReference,
  registration_user_status_histories: RegistrationUserStatusHistory,
  registration_business_profile_status_histories:
    RegistrationBusinessProfileStatusHistory,
  registration_outlet_status_histories: RegistrationOutletStatusHistory,

  // ----------------------------------
  // Geography
  // ----------------------------------
  regions: Region,
  cities: City,
  districts: District,
  sub_districts: SubDistrict,
  postal_codes: PostalCode,

  // ----------------------------------
  // Feature Access Control
  // ----------------------------------
  shipto_feature_segments: ShiptoFeatureSegment,
};

export type Db = RecursiveStatic<typeof DbSchema>;
